import { useState, useEffect, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useAuth, API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Package,
  LogOut,
  Settings as SettingsIcon,
  Key,
  Shield,
  Server,
  Database,
  CheckCircle2,
  XCircle,
  ExternalLink,
  Copy,
  Info,
  Cloud,
  HardDrive,
  Loader2,
  Trash2,
  Eye,
  EyeOff,
  RefreshCw,
  Upload,
  AlertTriangle,
  Building2,
  UserCog,
  Users,
  UserPlus,
  Mail,
  Crown,
  Rocket,
  Zap,
  ToggleLeft,
  ToggleRight,
  ShieldAlert,
} from "lucide-react";
import WeeklyDigestCard from "./WeeklyDigestCard";
import BrandingCard from "./BrandingCard";
import DetectionOverridesCard from "./DetectionOverridesCard";
import SyncScheduleCard from "@/components/SyncScheduleCard";
import SyncHistoryCard from "@/components/SyncHistoryCard";
import WindowsUpdateCatalogCard from "@/components/WindowsUpdateCatalogCard";
import WufBIntegrationCard from "@/components/WufBIntegrationCard";
import MspNotificationsCard from "@/components/MspNotificationsCard";
import FreshserviceIntegrationCard from "@/components/FreshserviceIntegrationCard";
import EnterpriseAppPermissionsCard from "@/components/EnterpriseAppPermissionsCard";
import ServiceTierCard from "@/components/ServiceTierCard";
import DeploymentPipelinesCard from "@/components/deployment/DeploymentPipelinesCard";
import { usePermissions } from "@/contexts/PermissionsContext";

// Pipeline templates are owned by DeploymentPipelinesCard. The Deployment
// Automation card must never send them back: its copy goes stale as soon as
// the pipelines card saves, and a resend would revert that save.
const withoutPipelineFields = (settings) => {
  const { stage_templates, default_stage_template_id, ...rest } = settings || {};
  return rest;
};

export default function SettingsPage() {
  const { user, logout, demoMode, getAccessToken, getArmToken } = useAuth();
  // PUT /deployment-automation/settings needs deploy_packages (403 otherwise).
  const { canDeployPackages, isAdmin } = usePermissions();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeSection = searchParams.get("section") || "general";
  const setActiveSection = (section) =>
    setSearchParams({ section }, { replace: true });
  const appsDbFileRef = useRef(null);
  const cveDbFileRef = useRef(null);
  
  // Tenant management state
  const [tenantInfo, setTenantInfo] = useState(null);
  const [resettingTenant, setResettingTenant] = useState(false);
  const [testingGraphConnection, setTestingGraphConnection] = useState(false);
  const [graphConnectionStatus, setGraphConnectionStatus] = useState(null);
  const [applyingRationalization, setApplyingRationalization] = useState(false);
  
  // User management state (RBAC)
  const [currentUserRole, setCurrentUserRole] = useState(null);
  // OS Patch cards are hidden for an Apps Management only tenant. Unknown
  // capabilities (still loading, or an older backend) keep them visible.
  const tenantHasOSPatch =
    !Array.isArray(currentUserRole?.capabilities) ||
    currentUserRole.capabilities.includes("os_patch_management");
  // Plans and the permissions each one's app registration holds, for the
  // super-admin permissions card.
  const [serviceTierCatalog, setServiceTierCatalog] = useState(null);
  const [tenantUsers, setTenantUsers] = useState([]);
  const [availableRoles, setAvailableRoles] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteDisplayName, setInviteDisplayName] = useState("");
  const [inviteRole, setInviteRole] = useState("viewer");
  const [invitingUser, setInvitingUser] = useState(false);
  
  // Super Admin state
  const [allTenants, setAllTenants] = useState([]);
  const [adminStats, setAdminStats] = useState(null);
  const [loadingTenants, setLoadingTenants] = useState(false);
  const [selectedTenantUsers, setSelectedTenantUsers] = useState({});
  // Offline / MSP SCCM tenant creation (super admin only)
  const OFFLINE_TENANT_BLANK = {
    display_name: "",
    admin_email: "",
    domain: "",
    technical_contact: "",
    linked_entra_tenant_id: "",
  };
  const [offlineTenantForm, setOfflineTenantForm] = useState(OFFLINE_TENANT_BLANK);
  const [creatingOfflineTenant, setCreatingOfflineTenant] = useState(false);
  const [createdOfflineTenant, setCreatedOfflineTenant] = useState(null);
  
  // Azure AD Configuration - use user's tenant ID from their session
  const [azureConfig, setAzureConfig] = useState({
    clientId: process.env.REACT_APP_AZURE_CLIENT_ID || "",
    tenantId: "", // Will be set from user session
  });
  
  // Update azureConfig when user changes (to show their tenant ID, not the app's)
  useEffect(() => {
    if (user?.tenantId) {
      setAzureConfig(prev => ({
        ...prev,
        tenantId: user.tenantId
      }));
    }
  }, [user?.tenantId]);
  
  // Database stats
  const [appsDbStats, setAppsDbStats] = useState(null);
  const [cveDbStats, setCveDbStats] = useState(null);
  const [wingetDbStats, setWingetDbStats] = useState(null);
  const [uploadingAppsDb, setUploadingAppsDb] = useState(false);
  const [uploadingCveDb, setUploadingCveDb] = useState(false);
  const [uploadingWingetDb, setUploadingWingetDb] = useState(false);
  const wingetDbFileRef = useRef(null);
  
  // Azure Blob Storage Configuration
  const [blobConfig, setBlobConfig] = useState({
    configured: false,
    storage_account_name: "",
    container_name: "intunewin-packages",
    auth_mode: "entra",
    storage_account_resource_id: null,
    last_upload_error: null,
  });
  const [blobForm, setBlobForm] = useState({
    auth_mode: "shared_key",
    storage_account_name: "",
    storage_subscription_id: "",
    storage_resource_group: "",
    connection_string: "",
    container_name: "intunewin-packages",
    storage_layout: "legacy",
    package_path_template: "Packages/{AppName}/{Version}/{Filename}",
    intunewin_path_template: "Intunewin/{AppName}/{Version}/{Filename}",
    evidence_path_template: "test-evidence/{PackageId}/{GUID}/{Filename}",
    filename_template: "",
    // ---- Private Repository (auto-update from blob) ----
    private_repo_enabled: false,
    private_repo_container: "privaterepo",
    private_repo_root: "",
    private_repo_path_template: "{AppFamily}/{Version}/{Filename}",
  });
  const [privateRepoStats, setPrivateRepoStats] = useState(null); // {family_count, version_count, scanned_at, issues}
  const [rescanningPrivateRepo, setRescanningPrivateRepo] = useState(false);
  const [showConnectionString, setShowConnectionString] = useState(false);
  const [testingConnection, setTestingConnection] = useState(false);
  const [savingBlobConfig, setSavingBlobConfig] = useState(false);
  const [grantingStorageAccess, setGrantingStorageAccess] = useState(false);
  const [testResult, setTestResult] = useState(null); // {success, message, needs_role_assignment}

  // Path template state — tokens catalogue, defaults and live preview
  const [pathTokens, setPathTokens] = useState([]);
  const [pathDefaults, setPathDefaults] = useState({ legacy: null, consolidated: null, filename: null });
  const [pathPreview, setPathPreview] = useState({ package: null, intunewin: null, evidence: null, filename: null });
  const packageTemplateRef = useRef(null);
  const intunewinTemplateRef = useRef(null);
  const evidenceTemplateRef = useRef(null);
  const filenameTemplateRef = useRef(null);
  // ALICE service-principal info in the customer's tenant — used by the
  // Entra-mode help panel so the admin can copy the exact Object ID into
  // Azure Portal IAM. Fetched lazily on first render of Entra mode.
  const [aliceSp, setAliceSp] = useState(null);
  const [aliceSpError, setAliceSpError] = useState(null);
  const [loadingAliceSp, setLoadingAliceSp] = useState(false);
  
  // Auto-update settings
  const [autoUpdateSettings, setAutoUpdateSettings] = useState({
    global_auto_update_enabled: false,
    check_interval_hours: 24
  });
  const [savingAutoUpdate, setSavingAutoUpdate] = useState(false);
  
  // Deployment Automation settings
  const [deploymentAutomation, setDeploymentAutomation] = useState({
    auto_progress_enabled: false,
    test_to_phase1_threshold: 100,
    phase1_to_phase2_threshold: 70,
    phase2_to_phase3_threshold: 70,
    check_interval_minutes: 15
  });
  const [savingDeploymentAutomation, setSavingDeploymentAutomation] = useState(false);
  const [checkingDeployments, setCheckingDeployments] = useState(false);
  
  const [healthStatus, setHealthStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkHealth();
    fetchBlobConfig();
    fetchPathTokens();
    fetchAutoUpdateSettings();
    fetchDeploymentAutomationSettings();
    fetchAppsDbStats();
    fetchCveDbStats();
    fetchWingetDbStats();
    fetchTenantInfo();
    fetchCurrentUserRole();
    fetchTenantUsers();
    fetchAvailableRoles();
    fetchPrivateRepoStats();
  }, []);
  
  // Fetch super admin data when user role is loaded
  useEffect(() => {
    if (currentUserRole?.is_super_admin) {
      fetchAllTenants();
      fetchAdminStats();
    }
  }, [currentUserRole?.is_super_admin]);

  // Lazily fetch ALICE's service-principal Object ID in this tenant whenever
  // the user is on Entra-mode storage config — drives the help panel.
  useEffect(() => {
    if (blobForm.auth_mode !== "entra") return;
    if (aliceSp || loadingAliceSp || aliceSpError) return;
    const run = async () => {
      setLoadingAliceSp(true);
      try {
        const headers = await getAuthHeaders();
        const r = await axios.get(`${API}/tenant/onboarding/alice-service-principal`, { headers });
        setAliceSp(r.data);
      } catch (e) {
        setAliceSpError(e.response?.data?.detail || e.message || "Could not look up ALICE service principal");
      } finally {
        setLoadingAliceSp(false);
      }
    };
    run();
  }, [blobForm.auth_mode]);

  // Live-preview path templates whenever the user edits them (debounced).
  // Only active when storage_layout === "template" to avoid pointless calls.
  useEffect(() => {
    if (blobForm.storage_layout !== "template") {
      setPathPreview({ package: null, intunewin: null, evidence: null, filename: null });
      return;
    }
    const handle = setTimeout(async () => {
      try {
        const headers = await getAuthHeaders();
        const r = await axios.post(
          `${API}/settings/azure-blob/path-preview`,
          {
            package_path_template: blobForm.package_path_template,
            intunewin_path_template: blobForm.intunewin_path_template,
            evidence_path_template: blobForm.evidence_path_template,
            filename_template: blobForm.filename_template || null,
          },
          { headers },
        );
        setPathPreview(r.data || {});
      } catch (e) {
        // Preview is best-effort; ignore failures
      }
    }, 250);
    return () => clearTimeout(handle);
  }, [
    blobForm.storage_layout,
    blobForm.package_path_template,
    blobForm.intunewin_path_template,
    blobForm.evidence_path_template,
    blobForm.filename_template,
  ]);

  // Insert a {Token} into the template input at the current cursor position.
  const insertTokenIntoTemplate = (field, token, inputRef) => {
    const el = inputRef?.current;
    const current = blobForm[field] || "";
    const placeholder = `{${token}}`;
    if (!el) {
      setBlobForm({ ...blobForm, [field]: current + placeholder });
      return;
    }
    const start = el.selectionStart ?? current.length;
    const end = el.selectionEnd ?? current.length;
    const next = current.slice(0, start) + placeholder + current.slice(end);
    setBlobForm({ ...blobForm, [field]: next });
    // Restore focus + cursor position after the inserted token
    requestAnimationFrame(() => {
      try {
        el.focus();
        const pos = start + placeholder.length;
        el.setSelectionRange(pos, pos);
      } catch (e) { /* no-op */ }
    });
  };
  
  const getAuthHeaders = async () => {
    if (demoMode) {
      return {};
    }
    try {
      const token = await getAccessToken();
      return token ? { Authorization: `Bearer ${token}` } : {};
    } catch (error) {
      console.error("Failed to get access token:", error);
      return {};
    }
  };
  
  // Fetch current user's role and permissions
  const fetchCurrentUserRole = async () => {
    if (demoMode) return;
    try {
      const headers = await getAuthHeaders();
      console.log("Fetching current user role with headers:", Object.keys(headers));
      const response = await axios.get(`${API}/users/me`, { headers });
      console.log("Current user role response:", JSON.stringify(response.data, null, 2));
      console.log("Permissions array:", response.data?.permissions);
      console.log("Has manage_users:", response.data?.permissions?.includes("manage_users"));
      setCurrentUserRole(response.data);
    } catch (error) {
      console.error("Error fetching current user role:", error.response?.data || error.message);
      // Set a default empty state so UI shows "no permission" rather than loading forever
      setCurrentUserRole({ permissions: [] });
    }
  };
  
  // Fetch all users in the tenant
  const fetchTenantUsers = async () => {
    if (demoMode) return;
    try {
      setLoadingUsers(true);
      const headers = await getAuthHeaders();
      const response = await axios.get(`${API}/users`, { headers });
      setTenantUsers(response.data);
    } catch (error) {
      console.error("Error fetching tenant users:", error);
    } finally {
      setLoadingUsers(false);
    }
  };
  
  // Fetch available roles
  const fetchAvailableRoles = async () => {
    try {
      const headers = await getAuthHeaders();
      const response = await axios.get(`${API}/users/roles`, { headers });
      setAvailableRoles(response.data);
    } catch (error) {
      console.error("Error fetching roles:", error);
    }
  };
  
  // Super Admin: Fetch all tenants
  const fetchAllTenants = async () => {
    if (demoMode) return;
    try {
      setLoadingTenants(true);
      const headers = await getAuthHeaders();
      const response = await axios.get(`${API}/tenant/admin/tenants`, { headers });
      setAllTenants(response.data.tenants || []);
    } catch (error) {
      // Silently fail for non-super-admins (403)
      if (error.response?.status !== 403) {
        console.error("Error fetching tenants:", error);
      }
    } finally {
      setLoadingTenants(false);
    }
  };
  
  // Super Admin: Fetch system stats
  const fetchAdminStats = async () => {
    if (demoMode) return;
    try {
      const headers = await getAuthHeaders();
      const response = await axios.get(`${API}/tenant/admin/stats`, { headers });
      setAdminStats(response.data);
    } catch (error) {
      // Silently fail for non-super-admins
      if (error.response?.status !== 403) {
        console.error("Error fetching admin stats:", error);
      }
    }
  };
  
  // Super Admin: Fetch users for a specific tenant
  const fetchTenantUsersAdmin = async (tenantId) => {
    try {
      const headers = await getAuthHeaders();
      const response = await axios.get(`${API}/tenant/admin/tenants/${tenantId}/users`, { headers });
      setSelectedTenantUsers(prev => ({ ...prev, [tenantId]: response.data }));
    } catch (error) {
      console.error("Error fetching tenant users:", error);
    }
  };
  
  // Super Admin: Delete a tenant
  const handleDeleteTenantAdmin = async (tenantId, displayName) => {
    if (!window.confirm(
      `⚠️ DELETE TENANT "${displayName}"?\n\n` +
      "This will PERMANENTLY delete:\n" +
      "• All tenant data and configuration\n" +
      "• All users in this tenant\n\n" +
      "This action cannot be undone!"
    )) {
      return;
    }
    
    try {
      const headers = await getAuthHeaders();
      await axios.delete(`${API}/tenant/admin/tenants/${tenantId}`, { headers });
      toast.success(`Tenant "${displayName}" deleted successfully`);
      fetchAllTenants();
      fetchAdminStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete tenant");
    }
  };
  
  // Super Admin: create an SCCM-mode tenant on a customer's behalf.
  //
  // The normal /tenant/register route derives tenant_id from the caller's
  // Entra token, which is exactly what an offline customer cannot
  // provide. This endpoint mints a synthetic tenant id instead and marks
  // the tenant SCCM-mode, so it gets rationalisation, automation
  // potential and packaging but no Intune deployment or OS Patch.
  const handleCreateOfflineTenant = async (e) => {
    e.preventDefault();
    const displayName = offlineTenantForm.display_name.trim();
    if (displayName.length < 2) {
      toast.error("Organisation name must be at least 2 characters");
      return;
    }

    // Only send fields the operator actually filled in — the backend
    // treats them as optional and blank strings would be stored verbatim.
    const payload = { display_name: displayName };
    ["admin_email", "domain", "technical_contact", "linked_entra_tenant_id"].forEach((key) => {
      const value = offlineTenantForm[key]?.trim();
      if (value) payload[key] = value;
    });

    try {
      setCreatingOfflineTenant(true);
      const headers = await getAuthHeaders();
      const response = await axios.post(
        `${API}/tenant/admin/offline-tenant`, payload, { headers },
      );
      setCreatedOfflineTenant(response.data);
      setOfflineTenantForm(OFFLINE_TENANT_BLANK);
      toast.success(`SCCM tenant "${displayName}" created`);
      fetchAllTenants();
      fetchAdminStats();
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(
        (typeof detail === "string" ? detail : detail?.message) ||
        "Failed to create offline tenant"
      );
    } finally {
      setCreatingOfflineTenant(false);
    }
  };

  // Invite a new user
  const handleInviteUser = async (e) => {
    e.preventDefault();
    if (!inviteEmail.trim()) {
      toast.error("Please enter an email address");
      return;
    }
    
    try {
      setInvitingUser(true);
      const headers = await getAuthHeaders();
      await axios.post(`${API}/users/invite`, {
        email: inviteEmail.trim(),
        display_name: inviteDisplayName.trim() || null,
        role: inviteRole,
      }, { headers });
      
      toast.success(`Invited ${inviteEmail} as ${availableRoles.find(r => r.value === inviteRole)?.display_name || inviteRole}`);
      setInviteEmail("");
      setInviteDisplayName("");
      setInviteRole("viewer");
      fetchTenantUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to invite user");
    } finally {
      setInvitingUser(false);
    }
  };
  
  // Update user role
  const handleUpdateUserRole = async (userEmail, newRole) => {
    try {
      const headers = await getAuthHeaders();
      await axios.put(`${API}/users/${encodeURIComponent(userEmail)}/role`, {
        role: newRole,
      }, { headers });
      
      toast.success(`Updated ${userEmail} role to ${availableRoles.find(r => r.value === newRole)?.display_name || newRole}`);
      fetchTenantUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update user role");
    }
  };
  
  // Remove user
  const handleRemoveUser = async (userEmail) => {
    if (!confirm(`Are you sure you want to remove ${userEmail} from this tenant?`)) {
      return;
    }
    
    try {
      const headers = await getAuthHeaders();
      await axios.delete(`${API}/users/${encodeURIComponent(userEmail)}`, { headers });
      
      toast.success(`Removed ${userEmail} from tenant`);
      fetchTenantUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to remove user");
    }
  };
  
  // Short plan names for the super-admin tenant list. Plans are changed from
  // the ALICE Admin portal.
  const SERVICE_TIER_OPTIONS = [
    { value: "full", label: "Full plan" },
    { value: "apps_patch_readonly", label: "Read-only OS Patch" },
    { value: "apps_only", label: "Apps only" },
  ];
  const serviceTierShortLabel = (tier) =>
    (SERVICE_TIER_OPTIONS.find((o) => o.value === tier) || SERVICE_TIER_OPTIONS[0]).label;

  // Super admins see which permissions the tenant's plan uses.
  useEffect(() => {
    if (!currentUserRole?.is_super_admin || serviceTierCatalog) return;
    axios
      .get(`${API}/tenant/onboarding/service-tiers`)
      .then((r) => setServiceTierCatalog(Array.isArray(r.data?.tiers) ? r.data.tiers : []))
      .catch(() => setServiceTierCatalog([]));
  }, [currentUserRole?.is_super_admin, serviceTierCatalog]);

  const fetchTenantInfo = async () => {
    if (demoMode) return;
    try {
      const headers = await getAuthHeaders();
      const response = await axios.get(`${API}/tenant/status`, { headers });
      setTenantInfo(response.data);
    } catch (error) {
      console.error("Failed to fetch tenant info:", error);
    }
  };
  
  const handleResetTenant = async () => {
    if (!window.confirm(
      "⚠️ DELETE TENANT AND ALL DATA?\n\n" +
      "This will PERMANENTLY delete:\n" +
      "• All discovered applications\n" +
      "• All packages and deployments\n" +
      "• App governance settings\n" +
      "• OS patch management data\n\n" +
      "Central databases (CVE, Winget, Apps) are NOT affected.\n\n" +
      "This action cannot be undone!"
    )) {
      return;
    }

    // Typed confirmation. The backend checks the same name (?confirm=), so a
    // stray click or replayed request can't drop the tenant.
    const orgName = (tenantInfo?.display_name || "").trim();
    if (!orgName) {
      toast.error("Couldn't read the organisation name. Refresh the page and try again.");
      return;
    }
    const typed = window.prompt(
      `To confirm, type the organisation name exactly:\n\n${orgName}`
    );
    if (typed === null) return;
    if (typed.trim().toLowerCase() !== orgName.toLowerCase()) {
      toast.error("The name didn't match. Nothing was deleted.");
      return;
    }

    setResettingTenant(true);
    try {
      const headers = await getAuthHeaders();
      await axios.delete(`${API}/tenant/reset`, {
        headers,
        params: { delete_data: true, confirm: typed.trim() },
      });
      toast.success("Tenant and all data deleted. Please sign out and sign back in to re-onboard.");
      setTenantInfo(null);
    } catch (error) {
      console.error("Failed to reset tenant:", error);
      toast.error(error.response?.data?.detail || "Failed to reset tenant");
    } finally {
      setResettingTenant(false);
    }
  };

  const [cancellingSync, setCancellingSync] = useState(false);
  const [forcingFullSweep, setForcingFullSweep] = useState(false);

  const handleForceFullSweep = async () => {
    if (!window.confirm(
      "Force a full CVE keyword sweep on the next sync?\n\n" +
      "This resets the per-CPE keyword cursors so the next NVD sync " +
      "re-pulls the entire keyword corpus for every tracked product. " +
      "Useful if NVD ships an affected-only CVE for a product you're " +
      "already watching and you need to surface it immediately.\n\n" +
      "The next sync will be slower (~10-15 min). All subsequent " +
      "syncs return to fast delta mode."
    )) {
      return;
    }

    setForcingFullSweep(true);
    try {
      const headers = await getAuthHeaders();
      const response = await axios.post(`${API}/cve/sync/force-full-sweep`, {}, { headers });
      const cleared = response.data?.cpe_states_cleared ?? 0;
      toast.success(
        `Full sweep armed — ${cleared} CPE cursor(s) cleared. ` +
        `The next CVE sync will do a full keyword pull.`
      );
    } catch (error) {
      console.error("Failed to force full sweep:", error);
      toast.error(error.response?.data?.detail || "Failed to force full sweep");
    } finally {
      setForcingFullSweep(false);
    }
  };

  const handleCancelSync = async () => {
    if (!window.confirm(
      "Cancel the current sync?\n\n" +
      "This will reset the sync status and allow you to start a new sync."
    )) {
      return;
    }
    
    setCancellingSync(true);
    try {
      const headers = await getAuthHeaders();
      const response = await axios.post(`${API}/discover/sync/cancel`, {}, { headers });
      toast.success(response.data?.message || "Sync cancelled");
    } catch (error) {
      console.error("Failed to cancel sync:", error);
      toast.error(error.response?.data?.detail || "Failed to cancel sync");
    } finally {
      setCancellingSync(false);
    }
  };
  
  const handleTestConnection = async () => {
    setTestingGraphConnection(true);
    setGraphConnectionStatus(null);
    try {
      const headers = await getAuthHeaders();
      const response = await axios.get(`${API}/tenant/test-connection`, { headers });
      setGraphConnectionStatus(response.data);
      if (response.data.status === "success") {
        toast.success("Connection successful!");
      } else {
        toast.error(response.data.message || "Connection failed");
      }
    } catch (error) {
      console.error("Connection test failed:", error);
      setGraphConnectionStatus({ status: "error", message: error.response?.data?.detail || "Connection test failed" });
      toast.error(error.response?.data?.detail || "Connection test failed");
    } finally {
      setTestingGraphConnection(false);
    }
  };
  
  const fetchAppsDbStats = async () => {
    try {
      const headers = await getAuthHeaders();
      const response = await axios.get(`${API}/discover/apps-database/stats`, { headers });
      setAppsDbStats(response.data);
    } catch (error) {
      console.error("Failed to fetch AppsDatabase stats:", error);
    }
  };
  
  const fetchCveDbStats = async () => {
    try {
      const headers = await getAuthHeaders();
      const response = await axios.get(`${API}/discover/cve-database/stats`, { headers });
      setCveDbStats(response.data);
    } catch (error) {
      console.error("Failed to fetch CVE database stats:", error);
    }
  };
  
  const fetchWingetDbStats = async () => {
    try {
      const headers = await getAuthHeaders();
      const response = await axios.get(`${API}/discover/winget-database/stats`, { headers });
      setWingetDbStats(response.data);
    } catch (error) {
      console.error("Failed to fetch Winget database stats:", error);
    }
  };
  
  const handleAppsDbUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    try {
      setUploadingAppsDb(true);
      const headers = await getAuthHeaders();
      
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(`${API}/discover/apps-database/import`, formData, {
        headers: { ...headers, 'Content-Type': 'multipart/form-data' }
      });
      
      toast.success(response.data.message);
      
      // Apply rationalization
      toast.info("Applying rationalization to detected apps...");
      await axios.post(`${API}/discover/apply-rationalization`, {}, { headers });
      toast.success("Rationalisation applied successfully");
      
      fetchAppsDbStats();
    } catch (error) {
      console.error("Error importing AppsDatabase:", error);
      toast.error(error.response?.data?.detail || "Failed to import AppsDatabase");
    } finally {
      setUploadingAppsDb(false);
      if (appsDbFileRef.current) appsDbFileRef.current.value = '';
    }
  };
  
  const handleReapplyRationalization = async () => {
    try {
      setApplyingRationalization(true);
      const headers = await getAuthHeaders();
      
      toast.info("Applying rationalization to detected apps...");
      const response = await axios.post(`${API}/discover/apply-rationalization`, {}, { headers });
      
      toast.success(`Rationalisation complete: ${response.data.matched_apps} apps matched`);
    } catch (error) {
      console.error("Error applying rationalization:", error);
      toast.error(error.response?.data?.detail || "Failed to apply rationalization");
    } finally {
      setApplyingRationalization(false);
    }
  };
  
  const handleCveDbUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    try {
      setUploadingCveDb(true);
      const headers = await getAuthHeaders();
      
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(`${API}/discover/cve-database/import`, formData, {
        headers: { ...headers, 'Content-Type': 'multipart/form-data' }
      });
      
      toast.success(response.data.message);
      
      // Apply CVE matching
      toast.info("Matching CVEs to detected apps...");
      await axios.post(`${API}/discover/apply-cve-matching`, {}, { headers });
      toast.success("CVE matching completed");
      
      fetchCveDbStats();
    } catch (error) {
      console.error("Error importing CVE database:", error);
      toast.error(error.response?.data?.detail || "Failed to import CVE database");
    } finally {
      setUploadingCveDb(false);
      if (cveDbFileRef.current) cveDbFileRef.current.value = '';
    }
  };
  
  const handleWingetDbUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    try {
      setUploadingWingetDb(true);
      const headers = await getAuthHeaders();
      
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(`${API}/discover/winget-database/import`, formData, {
        headers: { ...headers, 'Content-Type': 'multipart/form-data' }
      });
      
      toast.success(response.data.message);
      
      // Apply Winget matching
      toast.info("Matching Winget packages to detected apps...");
      await axios.post(`${API}/discover/apply-winget-matching`, {}, { headers });
      toast.success("Winget matching completed");
      
      fetchWingetDbStats();
    } catch (error) {
      console.error("Error importing Winget database:", error);
      toast.error(error.response?.data?.detail || "Failed to import Winget database");
    } finally {
      setUploadingWingetDb(false);
      if (wingetDbFileRef.current) wingetDbFileRef.current.value = '';
    }
  };

  const fetchAutoUpdateSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings/auto-update`);
      setAutoUpdateSettings(response.data);
    } catch (error) {
      console.error("Failed to fetch auto-update settings:", error);
    }
  };

  const saveAutoUpdateSettings = async () => {
    setSavingAutoUpdate(true);
    try {
      const response = await axios.post(
        `${API}/settings/auto-update?global_enabled=${autoUpdateSettings.global_auto_update_enabled}&check_interval_hours=${autoUpdateSettings.check_interval_hours}`
      );
      toast.success("Auto-update settings saved");
      setAutoUpdateSettings(response.data);
    } catch (error) {
      console.error("Failed to save auto-update settings:", error);
      toast.error("Failed to save settings");
    } finally {
      setSavingAutoUpdate(false);
    }
  };

  // Deployment Automation functions
  const fetchDeploymentAutomationSettings = async () => {
    try {
      const headers = await getAuthHeaders();
      const response = await axios.get(`${API}/deployment-automation/settings`, { headers });
      setDeploymentAutomation(withoutPipelineFields(response.data));
    } catch (error) {
      console.error("Failed to fetch deployment automation settings:", error);
    }
  };

  const saveDeploymentAutomationSettings = async () => {
    setSavingDeploymentAutomation(true);
    try {
      const headers = await getAuthHeaders();
      // Without stage_templates / default_stage_template_id the server leaves
      // the pipeline templates as they are.
      const response = await axios.put(
        `${API}/deployment-automation/settings`,
        withoutPipelineFields(deploymentAutomation),
        { headers }
      );
      toast.success("Deployment automation settings saved");
      setDeploymentAutomation(prev => ({ ...prev, ...withoutPipelineFields(response.data.settings) }));
    } catch (error) {
      console.error("Failed to save deployment automation settings:", error);
      toast.error(error.response?.data?.detail || "Failed to save deployment automation settings");
    } finally {
      setSavingDeploymentAutomation(false);
    }
  };

  const triggerDeploymentCheck = async () => {
    setCheckingDeployments(true);
    try {
      const headers = await getAuthHeaders();
      const response = await axios.post(`${API}/deployment-automation/check-now`, {}, { headers });
      const data = response.data;
      
      if (!data.automation_enabled) {
        toast.error(data.message || "Automation is disabled");
      } else if (data.packages_ready_for_phase1 > 0) {
        toast.success(`Found ${data.packages_ready_for_phase1} package(s) ready for Phase 1. Check completed.`);
      } else {
        toast.success("Check completed. No packages ready for auto-deployment.");
      }
    } catch (error) {
      console.error("Failed to check deployments:", error);
      toast.error(error.response?.data?.detail || "Failed to check deployments");
    } finally {
      setCheckingDeployments(false);
    }
  };

  const checkHealth = async () => {
    try {
      const response = await axios.get(`${API}/health`);
      setHealthStatus(response.data);
    } catch (error) {
      setHealthStatus({ status: "unhealthy" });
    } finally {
      setLoading(false);
    }
  };

  const [mergingDuplicates, setMergingDuplicates] = useState(false);
  const [normalizingNames, setNormalizingNames] = useState(false);
  const [cveHardResetting, setCveHardResetting] = useState(false);
  const [cveResyncing, setCveResyncing] = useState(false);
  const [cveHardResetInfo, setCveHardResetInfo] = useState(null);
  
  const mergeDuplicateApps = async () => {
    setMergingDuplicates(true);
    try {
      const headers = await getAuthHeaders();
      const response = await axios.post(`${API}/discover/merge-duplicates`, {}, { headers });
      const data = response.data;
      if (data.deleted_count > 0) {
        toast.success(`Merged ${data.merged_count} entries, deleted ${data.deleted_count} duplicates`);
      } else {
        toast.success("No duplicate entries found");
      }
    } catch (error) {
      console.error("Failed to merge duplicates:", error);
      toast.error(error.response?.data?.detail || "Failed to merge duplicates");
    } finally {
      setMergingDuplicates(false);
    }
  };

  const normalizeAppNames = async () => {
    setNormalizingNames(true);
    try {
      const headers = await getAuthHeaders();
      const response = await axios.post(`${API}/discover/normalize-names`, {}, { headers });
      const data = response.data;
      if (data.renamed_count > 0 || data.deleted_duplicates > 0) {
        toast.success(`Renamed ${data.renamed_count} entries, merged ${data.deleted_duplicates} duplicates`);
      } else {
        toast.success("All app names are already normalized");
      }
    } catch (error) {
      console.error("Failed to normalize names:", error);
      toast.error(error.response?.data?.detail || "Failed to normalize names");
    } finally {
      setNormalizingNames(false);
    }
  };

  const hardResetCveStore = async () => {
    // Double confirmation because this wipes 150k+ CPE-index rows and
    // forces the next NVD sync to do a full per-CPE re-pull.
    const confirmed = window.confirm(
      "⚠️  HARD RESET the CVE store?\n\n" +
      "This will:\n" +
      "  • Delete every row in cve_cpe_index (~150k)\n" +
      "  • Clear every per-CPE delta cursor\n" +
      "  • Force the NEXT CVE sync to do a FULL re-pull (slower + higher Cosmos RU)\n\n" +
      "Only run this when the 'Highest' column is blank across the board or " +
      "cve_records has drifted out of sync with cve_cpe_index."
    );
    if (!confirmed) return;
    setCveHardResetting(true);
    setCveHardResetInfo(null);
    try {
      const headers = await getAuthHeaders();
      const response = await axios.post(`${API}/cve/sync/hard-reset`, {}, { headers });
      const data = response.data;
      setCveHardResetInfo(data);
      toast.success(
        `CVE store reset: cleared ${data.cve_cpe_index_cleared} index rows and ` +
        `${data.cpe_cursors_cleared} delta cursors. Trigger a CVE sync next.`
      );
    } catch (error) {
      console.error("Failed to hard-reset CVE store:", error);
      toast.error(error.response?.data?.detail || "Failed to hard-reset CVE store");
    } finally {
      setCveHardResetting(false);
    }
  };

  const triggerCveResync = async () => {
    setCveResyncing(true);
    try {
      const headers = await getAuthHeaders();
      await axios.post(
        `${API}/cve/sync`,
        { include_nvd: true, include_epss: true, include_kev: true, background: true },
        { headers },
      );
      toast.success(
        "CVE sync started in background. Watch Sync History for progress; " +
        "the 'Highest' column will populate once the post-sync rematch completes."
      );
    } catch (error) {
      console.error("Failed to trigger CVE sync:", error);
      toast.error(error.response?.data?.detail || "Failed to trigger CVE sync");
    } finally {
      setCveResyncing(false);
    }
  };

  const fetchBlobConfig = async () => {
    try {
      const headers = await getAuthHeaders();
      const response = await axios.get(`${API}/settings/azure-blob`, { headers });
      setBlobConfig(response.data);
      if (response.data.configured) {
        // Try to parse subscription_id + resource_group from saved resource_id
        let sub = "";
        let rg = "";
        const rid = response.data.storage_account_resource_id || "";
        const m = rid.match(/^\/subscriptions\/([^/]+)\/resourceGroups\/([^/]+)\/providers\/Microsoft\.Storage\/storageAccounts\/([^/]+)/i);
        if (m) {
          sub = m[1];
          rg = m[2];
        }
        setBlobForm(prev => ({
          ...prev,
          auth_mode: response.data.auth_mode || "entra",
          storage_account_name: response.data.storage_account_name || "",
          storage_subscription_id: sub,
          storage_resource_group: rg,
          container_name: response.data.container_name || "intunewin-packages",
          storage_layout: response.data.storage_layout || "legacy",
          package_path_template:
            response.data.package_path_template || prev.package_path_template,
          intunewin_path_template:
            response.data.intunewin_path_template || prev.intunewin_path_template,
          evidence_path_template:
            response.data.evidence_path_template || prev.evidence_path_template,
          filename_template:
            response.data.filename_template || "",
          private_repo_enabled: !!response.data.private_repo_enabled,
          private_repo_container:
            response.data.private_repo_container || prev.private_repo_container || "privaterepo",
          private_repo_root:
            response.data.private_repo_root || "",
          private_repo_path_template:
            response.data.private_repo_path_template || prev.private_repo_path_template,
        }));
      }
    } catch (error) {
      console.error("Failed to fetch blob config:", error);
    }
  };

  // Load placeholder tokens + default templates once
  const fetchPathTokens = async () => {
    try {
      const headers = await getAuthHeaders();
      const response = await axios.get(`${API}/settings/azure-blob/path-tokens`, { headers });
      setPathTokens(response.data.tokens || []);
      setPathDefaults(response.data.defaults || { legacy: null, consolidated: null });
    } catch (error) {
      console.error("Failed to fetch path tokens:", error);
    }
  };

  // Client-side validation. Returns null if OK, otherwise an error string.
  const validateBlobForm = () => {
    const acct = (blobForm.storage_account_name || "").trim();
    const cname = (blobForm.container_name || "").trim();
    if (!/^[a-z0-9]{3,24}$/.test(acct)) {
      return (
        "Storage Account Name must be 3-24 lowercase letters/digits only " +
        "(no hyphens, no uppercase). It is the value at the very end of the " +
        "storage account's resource ID."
      );
    }
    // Container Name validation with actionable feedback. Empty is OK
    // (server defaults to 'intunewin-packages').
    if (cname) {
      const reasons = [];
      if (cname.length < 3) reasons.push("must be at least 3 characters");
      if (cname.length > 63) reasons.push("must be no more than 63 characters");
      if (/[A-Z]/.test(cname)) reasons.push("uppercase letters are not allowed — use lowercase only");
      if (/_/.test(cname)) reasons.push("underscores '_' are not allowed — use hyphens '-' instead");
      if (/[^a-z0-9-]/.test(cname)) reasons.push("only lowercase letters, digits and single hyphens are allowed");
      if (/^-/.test(cname)) reasons.push("must not start with a hyphen");
      if (/-$/.test(cname)) reasons.push("must not end with a hyphen");
      if (cname.includes("--")) reasons.push("must not contain consecutive hyphens '--'");
      if (reasons.length) {
        return (
          `Container Name "${cname}" is invalid: ${reasons.join("; ")}. ` +
          `(Container Name is the field at the top of the Azure Blob Storage card — ` +
          `not the folder-structure templates.)`
        );
      }
    }
    if (blobForm.auth_mode === "entra") {
      const sub = (blobForm.storage_subscription_id || "").trim();
      const rg = (blobForm.storage_resource_group || "").trim();
      if (!/^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/.test(sub)) {
        return "Subscription ID must be a GUID.";
      }
      if (!rg || rg.length > 90 || !/^[A-Za-z0-9._\-()]+$/.test(rg) || rg.endsWith(".")) {
        return "Resource Group is not a valid Azure resource group name.";
      }
      if (acct.toLowerCase() === rg.toLowerCase()) {
        return "Storage Account Name must be different from the Resource Group.";
      }
    } else {
      if (!blobForm.connection_string) {
        return "Connection String is required for shared-key mode.";
      }
    }
    // Storage layout / template validation (client-side)
    if (blobForm.storage_layout === "template") {
      const fields = [
        ["package_path_template", "Package path template"],
        ["intunewin_path_template", "Intunewin path template"],
        ["evidence_path_template", "Evidence path template"],
      ];
      for (const [key, label] of fields) {
        const tmpl = (blobForm[key] || "").trim();
        if (!tmpl) return `${label} is required when using template layout.`;
        if (tmpl.startsWith("/") || tmpl.startsWith("\\")) return `${label} must not start with '/'.`;
        if (/(^|\/)\.\.(\/|$)/.test(tmpl)) return `${label} must not contain '..'.`;
        if (!tmpl.includes("{Filename}")) return `${label} must include the {Filename} placeholder.`;
        const tokens = (tmpl.match(/\{([A-Za-z0-9_]+)\}/g) || []).map((s) => s.slice(1, -1));
        const known = new Set(pathTokens.map((t) => t.name));
        if (known.size > 0) {
          const unknown = tokens.filter((t) => !known.has(t));
          if (unknown.length) return `${label} has unknown token(s): ${unknown.join(", ")}`;
        }
      }
      // Filename template is OPTIONAL; only validate if provided.
      const fnTmpl = (blobForm.filename_template || "").trim();
      if (fnTmpl) {
        if (fnTmpl.includes("/") || fnTmpl.includes("\\")) {
          return "Filename template must not contain '/' or '\\'.";
        }
        if (fnTmpl.includes("..")) {
          return "Filename template must not contain '..'.";
        }
        const tokens = (fnTmpl.match(/\{([A-Za-z0-9_]+)\}/g) || []).map((s) => s.slice(1, -1));
        if (!tokens.length) {
          return "Filename template must include at least one {Token} (otherwise every file would have the same name).";
        }
        const known = new Set(pathTokens.map((t) => t.name));
        if (known.size > 0) {
          const unknown = tokens.filter((t) => !known.has(t));
          if (unknown.length) return `Filename template has unknown token(s): ${unknown.join(", ")}`;
          const forbidden = tokens.filter((t) => t === "Filename" || t === "GUID");
          if (forbidden.length) return `Filename template can't use token(s): ${forbidden.join(", ")}`;
        }
      }
    }
    return null;
  };

  const testBlobConnection = async () => {
    const err = validateBlobForm();
    if (err) {
      toast.error(err);
      return;
    }

    setTestingConnection(true);
    setTestResult(null);
    try {
      const headers = await getAuthHeaders();
      const response = await axios.post(`${API}/settings/azure-blob/test`, blobForm, { headers });
      setTestResult(response.data);
      if (response.data.success) {
        toast.success(response.data.message || "Connection successful");
      } else {
        toast.error(response.data.message || "Connection failed");
      }
    } catch (error) {
      const msg = error.response?.data?.detail || "Connection test failed";
      setTestResult({ success: false, message: msg });
      toast.error(msg);
    } finally {
      setTestingConnection(false);
    }
  };

  const saveBlobConfig = async () => {
    const err = validateBlobForm();
    if (err) {
      toast.error(err);
      return;
    }

    setSavingBlobConfig(true);
    try {
      const headers = await getAuthHeaders();
      const response = await axios.post(`${API}/settings/azure-blob`, blobForm, { headers });
      toast.success(response.data.message);
      setTestResult(null);
      fetchBlobConfig();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save configuration");
    } finally {
      setSavingBlobConfig(false);
    }
  };

  // ---- Private Repository helpers ----
  const fetchPrivateRepoStats = async () => {
    try {
      const headers = await getAuthHeaders();
      const response = await axios.get(`${API}/private-repo/families`, { headers });
      const families = response.data.families || [];
      setPrivateRepoStats({
        family_count: families.length,
        version_count: families.reduce((acc, f) => acc + (f.version_count || 0), 0),
        scanned_at: families[0]?.last_scanned_at || null,
        families,
      });
    } catch (error) {
      // 400 means not enabled — that's fine, we just clear stats
      setPrivateRepoStats(null);
    }
  };

  const rescanPrivateRepo = async () => {
    setRescanningPrivateRepo(true);
    try {
      const headers = await getAuthHeaders();
      const response = await axios.post(`${API}/private-repo/refresh`, {}, { headers });
      const issues = response.data.issues || [];
      toast.success(
        `Private Repository scanned: ${response.data.family_count} families, ` +
        `${response.data.version_count} versions` +
        (issues.length ? ` (${issues.length} skipped)` : "")
      );
      await fetchPrivateRepoStats();
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(detail || "Failed to scan Private Repository");
    } finally {
      setRescanningPrivateRepo(false);
    }
  };

  // Automated role assignment: ALICE grants 'Storage Blob Data Contributor'
  // to its own service principal on the configured storage account using
  // the signed-in admin's ARM token. This is the no-PowerShell remediation
  // path for tenants that hit the 403 on Test Connection.
  const grantStorageAccess = async () => {
    const err = validateBlobForm();
    if (err) {
      toast.error(err);
      return;
    }
    if (blobForm.auth_mode !== "entra") {
      toast.error("Role assignment is only used with Microsoft Entra ID auth mode.");
      return;
    }

    setGrantingStorageAccess(true);
    setTestResult(null);
    try {
      const headers = await getAuthHeaders();
      let armToken;
      try {
        armToken = await getArmToken();
      } catch (e) {
        toast.error(
          "Could not get Azure Resource Manager permission. Please retry " +
          "and accept the Microsoft consent prompt, or sign in as an Azure admin."
        );
        return;
      }

      const resp = await axios.post(
        `${API}/tenant/onboarding/assign-storage-role`,
        {
          storage_subscription_id: (blobForm.storage_subscription_id || "").trim(),
          storage_resource_group: (blobForm.storage_resource_group || "").trim(),
          storage_account_name: (blobForm.storage_account_name || "").trim().toLowerCase(),
          container_name: (blobForm.container_name || "intunewin-packages").trim(),
        },
        { headers: { ...headers, "X-Arm-Token": armToken } }
      );

      const data = resp.data || {};
      const verification = data.verification || {};
      if (data.status === "success" && verification.success) {
        toast.success(data.message || "ALICE was granted access successfully.");
        setTestResult({ success: true, message: verification.message || data.message });
      } else {
        toast.success(
          "Role assigned. Azure RBAC may take up to a minute to propagate — " +
          "click Test Connection in a few seconds to verify."
        );
        setTestResult({
          success: false,
          needs_role_assignment: false,
          message:
            "Role assigned (" + (data.role_assignment || "ok") +
            "). Re-test connection shortly — RBAC propagation can take up to 60 seconds.",
        });
      }
      fetchBlobConfig();
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (detail && typeof detail === "object" && detail.message) {
        setTestResult({ success: false, message: detail.message });
        toast.error(detail.message, { duration: 12000 });
      } else if (typeof detail === "string") {
        setTestResult({ success: false, message: detail });
        toast.error(detail);
      } else {
        toast.error("Failed to grant ALICE access to storage.");
      }
    } finally {
      setGrantingStorageAccess(false);
    }
  };

  const deleteBlobConfig = async () => {
    if (!window.confirm("Are you sure you want to remove Azure Blob Storage configuration?")) {
      return;
    }

    try {
      const headers = await getAuthHeaders();
      await axios.delete(`${API}/settings/azure-blob`, { headers });
      toast.success("Azure Blob Storage configuration removed");
      setBlobConfig({ configured: false, auth_mode: "shared_key" });
      setBlobForm({
        auth_mode: "shared_key",
        storage_account_name: "",
        storage_subscription_id: "",
        storage_resource_group: "",
        connection_string: "",
        container_name: "intunewin-packages",
        storage_layout: "legacy",
        package_path_template: "Packages/{AppName}/{Version}/{Filename}",
        intunewin_path_template: "Intunewin/{AppName}/{Version}/{Filename}",
        evidence_path_template: "test-evidence/{PackageId}/{GUID}/{Filename}",
        filename_template: "",
      });
      setTestResult(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to remove configuration");
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard");
  };

  return (
    <div className="min-h-screen bg-transparent">
      {/* Main Content */}
      <main className="p-6 max-w-4xl mx-auto">
        <div className="mb-8">
          <h1 className="text-2xl font-bold tracking-tight font-['Chivo'] text-white">Settings</h1>
          <p className="text-white/90">Configure your ALICE instance</p>
        </div>

        <div className="space-y-6">
          {activeSection === "general" && (<>
          {/* Demo Mode Alert */}
          {demoMode && (
            <Alert className="bg-blue-900/30 border-blue-700">
              <Info className="h-4 w-4 text-blue-400" />
              <AlertTitle className="text-blue-300">Demo Mode Active</AlertTitle>
              <AlertDescription className="text-blue-400">
                Azure AD is not configured. The application is running in demo mode with simulated authentication.
                Configure Azure AD credentials below to enable Microsoft authentication.
              </AlertDescription>
            </Alert>
          )}

          {/* System Status */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 font-['Chivo']">
                <Server className="h-5 w-5" />
                System Status
              </CardTitle>
              <CardDescription>Current health status of the application</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid gap-4 md:grid-cols-3">
                <div className="flex items-center gap-3 p-4 border">
                  {healthStatus?.status === "healthy" ? (
                    <CheckCircle2 className="h-5 w-5 text-green-600" />
                  ) : (
                    <XCircle className="h-5 w-5 text-red-600" />
                  )}
                  <div>
                    <p className="font-medium">API Server</p>
                    <p className="text-sm text-muted-foreground">
                      {healthStatus?.status === "healthy" ? "Online" : "Offline"}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-4 border">
                  <Database className="h-5 w-5 text-[#002FA7]" />
                  <div>
                    <p className="font-medium">Database</p>
                    <p className="text-sm text-muted-foreground">MongoDB</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-4 border">
                  {azureConfig.clientId ? (
                    <CheckCircle2 className="h-5 w-5 text-green-600" />
                  ) : (
                    <XCircle className="h-5 w-5 text-yellow-600" />
                  )}
                  <div>
                    <p className="font-medium">Azure AD</p>
                    <p className="text-sm text-muted-foreground">
                      {azureConfig.clientId ? "Configured" : "Not Configured"}
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Branding (per-tenant) */}
          <BrandingCard />

          {/* Weekly Digest Email (tenant admin opt-in) */}
          <WeeklyDigestCard />
          </>)}

          {activeSection === "integrations" && (<>
          {/* Managed Service Desk email notifications */}
          <MspNotificationsCard
            apiBase={API}
            authHeaders={getAuthHeaders}
          />

          {/* Freshservice ticket integration */}
          <FreshserviceIntegrationCard
            apiBase={API}
            authHeaders={getAuthHeaders}
          />

          {/* Azure AD Configuration */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 font-['Chivo']">
                <Key className="h-5 w-5" />
                Azure AD Configuration
              </CardTitle>
              <CardDescription>
                Configure Microsoft Azure Active Directory for authentication and Intune integration
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <Alert>
                <Info className="h-4 w-4" />
                <AlertTitle>Setup Instructions</AlertTitle>
                <AlertDescription className="mt-2 space-y-2">
                  <p>To configure Azure AD authentication, you need to:</p>
                  <ol className="list-decimal list-inside space-y-1 text-sm">
                    <li>Create an App Registration in Azure Portal</li>
                    <li>Configure the redirect URI as a Single-page application (SPA)</li>
                    <li>Add the required API permissions (see below)</li>
                    <li>Grant admin consent for your tenant</li>
                    <li>Set environment variables in your deployment</li>
                  </ol>
                </AlertDescription>
              </Alert>

              {/* Required Permissions */}
              <div className="border rounded-lg p-4 bg-white/10">
                <h4 className="font-semibold mb-3 flex items-center gap-2">
                  <Shield className="h-4 w-4" />
                  Required Microsoft Graph API Permissions (Delegated)
                </h4>
                <div className="space-y-4 text-sm">
                  <div>
                    <p className="font-medium text-white/90 mb-1">Core Authentication:</p>
                    <ul className="list-disc list-inside ml-2 text-muted-foreground space-y-0.5">
                      <li><code className="bg-white/20 px-1 rounded">User.Read</code> - Sign in and read user profile</li>
                    </ul>
                  </div>
                  <div>
                    <p className="font-medium text-white/90 mb-1">Package & Deploy Features:</p>
                    <ul className="list-disc list-inside ml-2 text-muted-foreground space-y-0.5">
                      <li><code className="bg-white/20 px-1 rounded">DeviceManagementApps.ReadWrite.All</code> - Deploy apps to Intune</li>
                      <li><code className="bg-white/20 px-1 rounded">DeviceManagementConfiguration.Read.All</code> - Read device configurations</li>
                      <li><code className="bg-white/20 px-1 rounded">Group.ReadWrite.All</code> - Create deployment groups (Go LIVE)</li>
                    </ul>
                  </div>
                  <div>
                    <p className="font-medium text-white/90 mb-1">Discover Feature:</p>
                    <ul className="list-disc list-inside ml-2 text-muted-foreground space-y-0.5">
                      <li><code className="bg-amber-100 px-1 rounded text-amber-800">DeviceManagementManagedDevices.Read.All</code> - Read detected apps from devices</li>
                    </ul>
                    <p className="text-xs text-amber-700 mt-1 ml-2">⚠️ Required for the Discover page to sync detected applications</p>
                  </div>
                </div>
              </div>

              <Separator />

              <div className="grid gap-4">
                <div className="space-y-2">
                  <Label htmlFor="clientId">Application (Client) ID</Label>
                  <div className="flex gap-2">
                    <Input
                      id="clientId"
                      value={azureConfig.clientId || "Not configured"}
                      readOnly
                      className="font-mono text-sm"
                      data-testid="client-id-input"
                    />
                    {azureConfig.clientId && (
                      <Button
                        variant="outline"
                        size="icon"
                        onClick={() => copyToClipboard(azureConfig.clientId)}
                      >
                        <Copy className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Environment variable: REACT_APP_AZURE_CLIENT_ID
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="tenantId">Your Organization's Directory (Tenant) ID</Label>
                  <div className="flex gap-2">
                    <Input
                      id="tenantId"
                      value={azureConfig.tenantId || "Not available (sign in required)"}
                      readOnly
                      className="font-mono text-sm"
                      data-testid="tenant-id-input"
                    />
                    {azureConfig.tenantId && (
                      <Button
                        variant="outline"
                        size="icon"
                        onClick={() => copyToClipboard(azureConfig.tenantId)}
                      >
                        <Copy className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    This is your organization's Azure AD tenant ID (from your login session)
                  </p>
                </div>

                <div className="space-y-2">
                  <Label>Redirect URI</Label>
                  <div className="flex gap-2">
                    <Input
                      value={window.location.origin}
                      readOnly
                      className="font-mono text-sm"
                    />
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => copyToClipboard(window.location.origin)}
                    >
                      <Copy className="h-4 w-4" />
                    </Button>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Add this URL to your Azure App Registration as a SPA redirect URI
                  </p>
                </div>
              </div>

              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={() => window.open("https://portal.azure.com/#view/Microsoft_AAD_RegisteredApps/ApplicationsListBlade", "_blank")}
                >
                  <ExternalLink className="h-4 w-4 mr-2" />
                  Open Azure Portal
                </Button>
                <Button
                  variant="outline"
                  onClick={() => window.open("https://learn.microsoft.com/en-us/azure/active-directory/develop/quickstart-register-app", "_blank")}
                >
                  <ExternalLink className="h-4 w-4 mr-2" />
                  Documentation
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Enterprise App Permissions — check and restore ALICE's Graph
              application permissions in this tenant, e.g. after the
              enterprise app was deleted and re-created by a plain sign-in.
              Tenant admins only; SCCM tenants have no enterprise app. */}
          {/* ALICE Plan — current plan, and approval of a plan change that
              needs a different ALICE app registration. */}
          {!demoMode &&
            currentUserRole?.permissions?.includes("manage_tenant") &&
            tenantInfo?.connection_type !== "sccm" && (
            <ServiceTierCard
              apiBase={API}
              authHeaders={getAuthHeaders}
              consentResult={searchParams.get("plan_consent")}
              consentError={searchParams.get("plan_consent_error")}
              onConsentHandled={() => setActiveSection("integrations")}
            />
          )}

          {!demoMode &&
            currentUserRole?.permissions?.includes("manage_tenant") &&
            tenantInfo?.connection_type !== "sccm" && (
            <EnterpriseAppPermissionsCard
              apiBase={API}
              authHeaders={getAuthHeaders}
              consentResult={searchParams.get("permissions_consent")}
              consentError={searchParams.get("permissions_consent_error")}
              onConsentHandled={() => setActiveSection("integrations")}
            />
          )}

          {/* API Permissions - Super Admin Only. Lists the application
              permissions held by the app registration this tenant's plan
              uses (from GET /tenant/onboarding/service-tiers). */}
          {currentUserRole?.is_super_admin && (() => {
            const tierKey = tenantInfo?.service_tier || "full";
            const tier = (serviceTierCatalog || []).find((t) => t.tier === tierKey);
            return (
          <Card data-testid="required-api-permissions-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 font-['Chivo']">
                <Shield className="h-5 w-5" />
                Required API Permissions
              </CardTitle>
              <CardDescription>
                {tier
                  ? `Plan: ${tier.label}. Application permissions on the ${tier.registration === "primary" ? "primary" : "reduced"} ALICE app registration${tier.client_id ? ` (${tier.client_id})` : ""}.`
                  : "These permissions are required for full functionality"}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="flex items-center justify-between p-3 border">
                  <div>
                    <p className="font-medium">User.Read</p>
                    <p className="text-sm text-muted-foreground">Sign in and read user profile (sign-in app registration)</p>
                  </div>
                  <Badge variant="secondary">Delegated</Badge>
                </div>
                {(tier?.permissions || []).map((perm) => (
                  <div key={perm.name} className="flex items-center justify-between p-3 border">
                    <div>
                      <p className="font-medium">{perm.name}</p>
                      <p className="text-sm text-muted-foreground">{perm.description}</p>
                    </div>
                    <Badge variant="secondary">Application</Badge>
                  </div>
                ))}
                {!tier && (
                  <p className="text-sm text-muted-foreground">Loading plan permissions…</p>
                )}
              </div>
            </CardContent>
          </Card>
            );
          })()}

          {/* Windows Update for Business Reporting — moved from
              OS Patch Management > Reports (now Discover) so every
              third-party integration lives in one place. */}
          {tenantHasOSPatch && <WufBIntegrationCard />}
          </>)}

          {activeSection === "automation" && (<>
          {/* Auto-Update Settings */}
          <Card data-testid="auto-update-config-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 font-['Chivo']">
                <RefreshCw className="h-5 w-5" />
                Auto-Update Settings
                {autoUpdateSettings.global_auto_update_enabled && (
                  <Badge variant="success" className="ml-2 bg-green-100 text-green-800">
                    <CheckCircle2 className="h-3 w-3 mr-1" />
                    Enabled
                  </Badge>
                )}
              </CardTitle>
              <CardDescription>
                Automatically check for and package new versions of your apps
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <Alert>
                <Info className="h-4 w-4" />
                <AlertTitle>How Auto-Updates Work</AlertTitle>
                <AlertDescription className="mt-2 space-y-2">
                  <ul className="list-disc list-inside space-y-1 text-sm">
                    <li><strong>Version checking:</strong> Compares your packaged versions against the apps catalog</li>
                    <li><strong>Per-package control:</strong> Enable/disable auto-updates for individual packages</li>
                    <li><strong>Auto-packaging:</strong> When enabled, new versions are automatically packaged</li>
                    <li><strong>Auto-deploy:</strong> Updated packages can be auto-deployed to previous test groups</li>
                  </ul>
                </AlertDescription>
              </Alert>
              
              <div className="space-y-4">
                <div className="flex items-center justify-between p-4 border rounded-lg">
                  <div>
                    <Label className="text-base font-medium">Global Auto-Update</Label>
                    <p className="text-sm text-muted-foreground mt-1">
                      Enable auto-updates for all packages by default
                    </p>
                  </div>
                  <button
                    onClick={() => setAutoUpdateSettings(prev => ({
                      ...prev,
                      global_auto_update_enabled: !prev.global_auto_update_enabled
                    }))}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      autoUpdateSettings.global_auto_update_enabled ? 'bg-green-500' : 'bg-white/30'
                    }`}
                    data-testid="global-auto-update-toggle"
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        autoUpdateSettings.global_auto_update_enabled ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>
                
                <Button 
                  onClick={saveAutoUpdateSettings}
                  disabled={savingAutoUpdate}
                  className="w-full"
                  data-testid="save-auto-update-btn"
                >
                  {savingAutoUpdate ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    "Save Auto-Update Settings"
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Detection Overrides — retargets Intune detection $AppName at
              the ARP DisplayName the installer actually writes. See
              server._override_detection_app_name / settings_routes
              /detection-overrides. */}
          <DetectionOverridesCard />
          </>)}

          {activeSection === "storage" && (<>
          {/* Azure Blob Storage Configuration */}
          <Card data-testid="azure-blob-config-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 font-['Chivo']">
                <Cloud className="h-5 w-5" />
                Azure Blob Storage
                {blobConfig.configured && (
                  <Badge variant="success" className="ml-2 bg-green-100 text-green-800">
                    <CheckCircle2 className="h-3 w-3 mr-1" />
                    Configured
                  </Badge>
                )}
              </CardTitle>
              <CardDescription>
                Store packages in Azure Blob Storage to free up local disk space and enable persistent storage
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <Alert>
                <Info className="h-4 w-4" />
                <AlertTitle>Tenant-Wide Storage Configuration</AlertTitle>
                <AlertDescription className="mt-2 space-y-2">
                  <ul className="list-disc list-inside space-y-1 text-sm">
                    <li><strong>Shared across tenant:</strong> All users in this organization will use this storage</li>
                    <li><strong>Free up disk space:</strong> Packages are uploaded to Azure and deleted locally</li>
                    <li><strong>Persistent storage:</strong> Packages remain available even after app restarts</li>
                    <li><strong>Secure downloads:</strong> Time-limited SAS URLs for package downloads</li>
                  </ul>
                </AlertDescription>
              </Alert>

              <Separator />

              {/* Auth mode toggle */}
              <div className="space-y-2">
                <Label>Authentication Method</Label>
                <div className="flex gap-2 flex-wrap" data-testid="blob-auth-mode-toggle">
                  <Button
                    type="button"
                    variant={blobForm.auth_mode === "shared_key" ? "default" : "outline"}
                    size="sm"
                    onClick={() => setBlobForm({ ...blobForm, auth_mode: "shared_key" })}
                    data-testid="blob-auth-mode-shared-key"
                  >
                    Connection string (recommended)
                  </Button>
                  <Button
                    type="button"
                    variant={blobForm.auth_mode === "entra" ? "default" : "outline"}
                    size="sm"
                    onClick={() => setBlobForm({ ...blobForm, auth_mode: "entra" })}
                    data-testid="blob-auth-mode-entra"
                  >
                    Microsoft Entra ID (advanced)
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground">
                  {blobForm.auth_mode === "entra"
                    ? "ALICE authenticates with your Entra ID — no shared keys to rotate. Requires the 'Storage Blob Data Contributor' role to be assigned to ALICE's service principal on this storage account."
                    : "Paste the storage account's connection string. Quickest path — works with any account that can read access keys. Recommended for most customers."}
                </p>
              </div>

              {/* Entra-mode help panel: shows the exact role + Object ID the
                  admin needs to add manually in the Azure Portal. */}
              {blobForm.auth_mode === "entra" && (
                <Alert data-testid="alice-sp-info-panel" className="bg-blue-50 border-blue-200">
                  <Shield className="h-4 w-4 text-blue-700" />
                  <AlertTitle className="text-blue-900">Grant ALICE access to this storage account</AlertTitle>
                  <AlertDescription className="mt-2 space-y-3 text-blue-900">
                    <p className="text-sm">
                      Add a single role assignment in Azure Portal — one minute, no PowerShell:
                    </p>
                    <ol className="list-decimal list-inside text-sm space-y-1">
                      <li>Open this storage account → <strong>Access control (IAM)</strong> → <strong>+ Add → Add role assignment</strong>.</li>
                      <li>
                        <strong>Role:</strong>{" "}
                        <code className="bg-white px-1.5 py-0.5 rounded border border-blue-200 font-mono text-xs">
                          Storage Blob Data Contributor
                        </code>
                      </li>
                      <li>
                        <strong>Assign access to:</strong> <em>User, group, or service principal</em>, then paste the
                        Object ID below into the "Select members" search.
                      </li>
                      <li>Click <strong>Review + assign</strong>.</li>
                    </ol>

                    <div className="rounded-md border border-blue-200 bg-white p-3 text-blue-950">
                      {loadingAliceSp ? (
                        <div className="flex items-center gap-2 text-sm">
                          <Loader2 className="h-4 w-4 animate-spin" />
                          Looking up ALICE in your tenant…
                        </div>
                      ) : aliceSp ? (
                        <div className="space-y-2 text-sm">
                          <div className="flex items-center justify-between gap-2">
                            <div>
                              <div className="text-xs uppercase tracking-wide text-blue-700">ALICE display name</div>
                              <div className="font-medium" data-testid="alice-sp-display-name">{aliceSp.display_name}</div>
                            </div>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => copyToClipboard(aliceSp.display_name)}
                              data-testid="copy-alice-sp-display-name"
                            >
                              Copy
                            </Button>
                          </div>
                          <div className="flex items-center justify-between gap-2">
                            <div className="min-w-0">
                              <div className="text-xs uppercase tracking-wide text-blue-700">Service principal Object ID</div>
                              <code className="font-mono text-xs break-all" data-testid="alice-sp-object-id">
                                {aliceSp.object_id}
                              </code>
                            </div>
                            <Button
                              type="button"
                              variant="default"
                              size="sm"
                              onClick={() => copyToClipboard(aliceSp.object_id)}
                              data-testid="copy-alice-sp-object-id"
                            >
                              Copy Object ID
                            </Button>
                          </div>
                          <div className="text-xs text-blue-800">
                            (Equivalent: Microsoft Entra ID → Enterprise applications → search for{" "}
                            <em>{aliceSp.display_name}</em>.)
                          </div>
                        </div>
                      ) : (
                        <div className="text-sm">
                          <p className="font-medium">Could not look up ALICE in your tenant.</p>
                          <p className="text-xs mt-1">{aliceSpError}</p>
                          <p className="text-xs mt-1">
                            In Azure Portal IAM, search for the display name you set during onboarding
                            (default: <em>ALICE - Intune Package Manager</em>) and assign{" "}
                            <strong>Storage Blob Data Contributor</strong>.
                          </p>
                        </div>
                      )}
                    </div>

                    <p className="text-xs">
                      <strong>Tip:</strong> if your account holds <em>Owner</em> or
                      <em> User Access Administrator</em> on this storage account, you can skip the portal
                      and click <strong>Grant ALICE access</strong> below — ALICE will create the role
                      assignment for you. Otherwise, ask an Azure admin to add the role manually using the
                      Object ID above. Either way works.
                    </p>
                  </AlertDescription>
                </Alert>
              )}

              {/* Setup Instructions */}
              {blobForm.auth_mode === "entra" ? (
                <div className="space-y-4">
                  <h4 className="font-medium">Where to find these values</h4>
                  <ol className="list-decimal list-inside space-y-2 text-sm text-muted-foreground">
                    <li><strong>Subscription ID</strong> — <a href="https://portal.azure.com" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline">Azure Portal</a> → Subscriptions → click your subscription → copy the GUID at the top.</li>
                    <li><strong>Resource Group</strong> — the resource group that contains your storage account (Storage account → Overview → "Resource group").</li>
                    <li><strong>Storage Account Name</strong> — the storage account itself; 3-24 lowercase letters/digits.</li>
                  </ol>
                </div>
              ) : (
                <div className="space-y-4">
                  <h4 className="font-medium">Setup Instructions</h4>
                  <ol className="list-decimal list-inside space-y-2 text-sm text-muted-foreground">
                    <li>Go to <a href="https://portal.azure.com" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline">Azure Portal</a></li>
                    <li>Storage Account → <strong>Security + networking → Access keys</strong></li>
                    <li>Copy the <strong>Connection string</strong> (under key1 or key2)</li>
                    <li>Paste it below and click "Test Connection"</li>
                  </ol>
                </div>
              )}

              <Separator />

              {/* Configuration Form */}
              <div className="grid gap-4">
                {blobForm.auth_mode === "entra" && (
                  <>
                    <div className="space-y-2">
                      <Label htmlFor="storageSubscriptionId">Subscription ID</Label>
                      <Input
                        id="storageSubscriptionId"
                        placeholder="00000000-0000-0000-0000-000000000000"
                        value={blobForm.storage_subscription_id}
                        onChange={(e) => setBlobForm({ ...blobForm, storage_subscription_id: e.target.value })}
                        data-testid="storage-subscription-id-input"
                      />
                      <p className="text-xs text-muted-foreground">Azure Portal → Subscriptions → copy the Subscription ID GUID.</p>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="storageResourceGroup">Resource Group</Label>
                      <Input
                        id="storageResourceGroup"
                        placeholder="rg-alice-prod"
                        value={blobForm.storage_resource_group}
                        onChange={(e) => setBlobForm({ ...blobForm, storage_resource_group: e.target.value })}
                        data-testid="storage-resource-group-input"
                      />
                      <p className="text-xs text-muted-foreground">The resource group that contains your storage account.</p>
                    </div>
                  </>
                )}

                <div className="space-y-2">
                  <Label htmlFor="storageAccountName">Storage Account Name</Label>
                  <Input
                    id="storageAccountName"
                    placeholder="camwoodinternal"
                    value={blobForm.storage_account_name}
                    onChange={(e) => setBlobForm({ ...blobForm, storage_account_name: e.target.value })}
                    data-testid="storage-account-name-input"
                  />
                  <p className="text-xs text-muted-foreground">
                    3-24 lowercase letters and digits only. <strong>Not the resource group.</strong> It's the value at the very end of the storage account's resource ID.
                  </p>
                </div>

                {blobForm.auth_mode === "shared_key" && (
                  <div className="space-y-2">
                    <Label htmlFor="connectionString">Connection String</Label>
                    <div className="flex gap-2">
                      <div className="relative flex-1">
                        <Input
                          id="connectionString"
                          type={showConnectionString ? "text" : "password"}
                          placeholder="DefaultEndpointsProtocol=https;AccountName=..."
                          value={blobForm.connection_string}
                          onChange={(e) => setBlobForm({ ...blobForm, connection_string: e.target.value })}
                          className="pr-10 font-mono text-sm"
                          data-testid="connection-string-input"
                        />
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="absolute right-0 top-0 h-full px-3"
                          onClick={() => setShowConnectionString(!showConnectionString)}
                        >
                          {showConnectionString ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </Button>
                      </div>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Found in Azure Portal → Storage Account → Access keys → Connection string
                    </p>
                  </div>
                )}

                <div className="space-y-2">
                  <Label htmlFor="containerName">Container Name</Label>
                  <Input
                    id="containerName"
                    placeholder="intunewin-packages"
                    value={blobForm.container_name}
                    onChange={(e) => setBlobForm({ ...blobForm, container_name: e.target.value })}
                    data-testid="container-name-input"
                  />
                  <p className="text-xs text-muted-foreground">
                    3-63 chars, <strong>lowercase letters, digits and single hyphens only</strong> (no underscores, no spaces, no uppercase). Container will be created automatically if it doesn't exist.
                  </p>
                </div>
              </div>

              <Separator />

              {/* ------------------------------------------------------------------
                  Folder Structure (storage_layout)
                  Per-tenant control over how blobs are organised. Defaults to
                  the historical layout so existing customers see no change.
                  ------------------------------------------------------------------ */}
              <div className="space-y-4" data-testid="storage-layout-section">
                <div>
                  <div className="flex items-center gap-2">
                    <Label className="text-base font-medium">Folder Structure</Label>
                    <Badge variant="secondary" className="text-xs">
                      {blobForm.storage_layout === "template" ? "Custom template" : "Default"}
                    </Badge>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    Control how packages, .intunewin files and test evidence are organised inside your container.
                  </p>
                </div>

                <div className="flex gap-2 flex-wrap" data-testid="storage-layout-toggle">
                  <Button
                    type="button"
                    variant={blobForm.storage_layout === "legacy" ? "default" : "outline"}
                    size="sm"
                    onClick={() => setBlobForm({ ...blobForm, storage_layout: "legacy" })}
                    data-testid="storage-layout-legacy"
                  >
                    Default layout
                  </Button>
                  <Button
                    type="button"
                    variant={blobForm.storage_layout === "template" ? "default" : "outline"}
                    size="sm"
                    onClick={() => setBlobForm({ ...blobForm, storage_layout: "template" })}
                    data-testid="storage-layout-template"
                  >
                    Custom template
                  </Button>
                  {blobForm.storage_layout === "template" && pathDefaults?.consolidated && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() =>
                        setBlobForm({
                          ...blobForm,
                          package_path_template: pathDefaults.consolidated.package,
                          intunewin_path_template: pathDefaults.consolidated.intunewin,
                          evidence_path_template: pathDefaults.consolidated.evidence,
                        })
                      }
                      data-testid="storage-layout-preset-consolidated"
                      title="Single folder per app: Packages/{Publisher}_{AppName}_{Version}_{Revision}/Package|Intune|Documentation"
                    >
                      Use consolidated preset
                    </Button>
                  )}
                  {blobForm.storage_layout === "template" && pathDefaults?.legacy && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() =>
                        setBlobForm({
                          ...blobForm,
                          package_path_template: pathDefaults.legacy.package,
                          intunewin_path_template: pathDefaults.legacy.intunewin,
                          evidence_path_template: pathDefaults.legacy.evidence,
                        })
                      }
                      data-testid="storage-layout-preset-legacy"
                    >
                      Reset to default
                    </Button>
                  )}
                </div>

                {blobForm.storage_layout === "legacy" && (
                  <Alert>
                    <Info className="h-4 w-4" />
                    <AlertTitle>Using the default layout</AlertTitle>
                    <AlertDescription className="mt-2 text-sm space-y-1">
                      <div><code className="bg-slate-100 px-1.5 py-0.5 rounded text-xs">Packages/&lt;AppName&gt;/&lt;Version&gt;/package.zip</code></div>
                      <div><code className="bg-slate-100 px-1.5 py-0.5 rounded text-xs">Intunewin/&lt;AppName&gt;/&lt;Version&gt;/package.intunewin</code></div>
                      <div><code className="bg-slate-100 px-1.5 py-0.5 rounded text-xs">test-evidence/&lt;PackageId&gt;/&lt;GUID&gt;/&lt;file&gt;</code></div>
                      <div className="text-muted-foreground pt-1">
                        Switch to <strong>Custom template</strong> to choose your own layout.
                      </div>
                    </AlertDescription>
                  </Alert>
                )}

                {blobForm.storage_layout === "template" && (
                  <div className="space-y-5">
                    <Alert className="bg-blue-50 border-blue-200">
                      <Info className="h-4 w-4 text-blue-700" />
                      <AlertTitle className="text-blue-900">Custom template</AlertTitle>
                      <AlertDescription className="mt-2 text-sm text-blue-900 space-y-2">
                        <p>
                          Build a path with <strong>placeholders</strong> like <code className="bg-white px-1 rounded text-xs">{"{AppName}"}</code> or <code className="bg-white px-1 rounded text-xs">{"{Publisher}_{AppName}_{Version}_{Revision}"}</code>.
                          Each template must end with <code className="bg-white px-1 rounded text-xs">{"{Filename}"}</code>.
                        </p>
                        <p className="text-xs text-blue-800">
                          Tokens with no value at upload time (e.g. missing Revision) are skipped, and surrounding separator characters are collapsed automatically.
                        </p>
                      </AlertDescription>
                    </Alert>

                    {/* ---------- Filename template (optional) ---------- */}
                    <div className="space-y-2 rounded-md border border-white/15 bg-white/5 p-4" data-testid="filename-template-row">
                      <div className="flex items-center gap-2 flex-wrap">
                        <Label htmlFor="filename_template" className="font-medium">
                          Package filename
                        </Label>
                        <Badge variant="outline" className="text-[10px] uppercase tracking-wide">
                          Optional
                        </Badge>
                        {pathDefaults?.filename && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="h-7 px-2 text-xs"
                            onClick={() =>
                              setBlobForm({ ...blobForm, filename_template: pathDefaults.filename })
                            }
                            data-testid="filename-template-use-default"
                          >
                            Use suggested ({pathDefaults.filename})
                          </Button>
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground">
                        Rename the package <strong>file itself</strong> before upload. Applied to both <code className="text-[11px] bg-white/10 px-1 rounded">.zip</code> and <code className="text-[11px] bg-white/10 px-1 rounded">.intunewin</code>; the extension is added automatically. Leave blank to keep the historical filenames (<code className="text-[11px] bg-white/10 px-1 rounded">{"<App>_<Version>_PSADT.zip"}</code> and the IntuneWin tool's default).
                      </p>
                      <div className="flex gap-2 items-start">
                        <Input
                          id="filename_template"
                          ref={filenameTemplateRef}
                          value={blobForm.filename_template || ""}
                          onChange={(e) => setBlobForm({ ...blobForm, filename_template: e.target.value })}
                          className="font-mono text-sm flex-1"
                          placeholder="{Publisher}_{AppName}_{Version}_{Revision}"
                          data-testid="filename-template-input"
                        />
                        <Select
                          onValueChange={(token) =>
                            insertTokenIntoTemplate("filename_template", token, filenameTemplateRef)
                          }
                        >
                          <SelectTrigger className="w-44" data-testid="filename-template-token-picker">
                            <SelectValue placeholder="Insert token…" />
                          </SelectTrigger>
                          <SelectContent>
                            {pathTokens
                              .filter((t) => t.name !== "Filename" && t.name !== "GUID")
                              .map((t) => (
                                <SelectItem key={t.name} value={t.name}>
                                  <span className="font-mono text-xs">{`{${t.name}}`}</span>
                                  <span className="text-xs text-muted-foreground ml-2">{t.description}</span>
                                </SelectItem>
                              ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div
                        className={`text-xs rounded-md border px-2.5 py-2 font-mono break-all ${
                          pathPreview?.filename?.ok === false
                            ? "bg-red-50 border-red-200 text-red-800"
                            : "bg-white/10 border-white/15 text-white"
                        }`}
                        data-testid="filename-template-preview"
                      >
                        {pathPreview?.filename?.ok === false ? (
                          <span>⚠️ {pathPreview.filename.error}</span>
                        ) : pathPreview?.filename?.zip ? (
                          <div className="space-y-1">
                            <div>
                              <span className="text-white/60 mr-1">PSADT zip:</span>
                              {pathPreview.filename.zip}
                            </div>
                            <div>
                              <span className="text-white/60 mr-1">IntuneWin:</span>
                              {pathPreview.filename.intunewin}
                            </div>
                          </div>
                        ) : (
                          <span className="text-white/50 italic">
                            Leave blank to keep the default filename.
                          </span>
                        )}
                      </div>
                    </div>

                    {[
                      {
                        field: "package_path_template",
                        label: "Package path",
                        helper: ".zip files produced by PSADT packaging",
                        ref: packageTemplateRef,
                        previewKey: "package",
                        testId: "package-template",
                      },
                      {
                        field: "intunewin_path_template",
                        label: "Intunewin path",
                        helper: "Encrypted .intunewin files",
                        ref: intunewinTemplateRef,
                        previewKey: "intunewin",
                        testId: "intunewin-template",
                      },
                      {
                        field: "evidence_path_template",
                        label: "Test evidence path",
                        helper: "Comments, test steps and screenshots uploaded during approval",
                        ref: evidenceTemplateRef,
                        previewKey: "evidence",
                        testId: "evidence-template",
                      },
                    ].map(({ field, label, helper, ref, previewKey, testId }) => {
                      const preview = pathPreview?.[previewKey];
                      return (
                        <div key={field} className="space-y-2" data-testid={`${testId}-row`}>
                          <Label htmlFor={field}>{label}</Label>
                          <div className="flex gap-2 items-start">
                            <Input
                              id={field}
                              ref={ref}
                              value={blobForm[field] || ""}
                              onChange={(e) => setBlobForm({ ...blobForm, [field]: e.target.value })}
                              className="font-mono text-sm flex-1"
                              data-testid={`${testId}-input`}
                              placeholder="Packages/{AppName}/{Version}/{Filename}"
                            />
                            <Select
                              onValueChange={(token) => insertTokenIntoTemplate(field, token, ref)}
                            >
                              <SelectTrigger className="w-44" data-testid={`${testId}-token-picker`}>
                                <SelectValue placeholder="Insert token…" />
                              </SelectTrigger>
                              <SelectContent>
                                {pathTokens.map((t) => (
                                  <SelectItem key={t.name} value={t.name}>
                                    <span className="font-mono text-xs">{`{${t.name}}`}</span>
                                    <span className="text-xs text-muted-foreground ml-2">{t.description}</span>
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          <p className="text-xs text-muted-foreground">{helper}</p>
                          <div
                            className={`text-xs rounded-md border px-2.5 py-2 font-mono break-all ${
                              preview?.ok === false
                                ? "bg-red-50 border-red-200 text-red-800"
                                : "bg-white/10 border-white/15 text-white"
                            }`}
                            data-testid={`${testId}-preview`}
                          >
                            {preview?.ok === false ? (
                              <span>⚠️ {preview.error}</span>
                            ) : preview?.preview ? (
                              <span>
                                <span className="text-white/60 not-italic mr-1">Preview:</span>
                                {preview.preview}
                              </span>
                            ) : (
                              <span className="text-white/50 italic">Preview will appear here…</span>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              <Separator />

              {/* ------------------------------------------------------------------
                  Private Repository (auto-update from blob)
                  Tenants point ALICE at a dedicated container (default: privaterepo)
                  whose folder structure encodes app family + version. ALICE then
                  treats every new version folder as an auto-update trigger.
                  ------------------------------------------------------------------ */}
              <div className="space-y-4" data-testid="private-repo-section">
                <div className="flex items-center justify-between gap-4 flex-wrap">
                  <div>
                    <div className="flex items-center gap-2">
                      <Label className="text-base font-medium">Private Repository</Label>
                      <Badge variant="secondary" className="text-xs">
                        {blobForm.private_repo_enabled ? "Enabled" : "Disabled"}
                      </Badge>
                      <Badge variant="outline" className="text-[10px] uppercase tracking-wide">
                        Auto-update from blob
                      </Badge>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1 max-w-xl">
                      Drop installers into <code className="bg-white/10 text-white/90 px-1 rounded text-xs">{"{Container}/{AppFamily}/{Version}/{Filename}"}</code>{" "}
                      and ALICE will surface them on the Discover page as <strong>Package</strong> actions,
                      then automatically repackage future versions using the previous package's configuration.
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setBlobForm({ ...blobForm, private_repo_enabled: !blobForm.private_repo_enabled })}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      blobForm.private_repo_enabled ? "bg-green-500" : "bg-white/30"
                    }`}
                    data-testid="private-repo-toggle"
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        blobForm.private_repo_enabled ? "translate-x-6" : "translate-x-1"
                      }`}
                    />
                  </button>
                </div>

                {blobForm.private_repo_enabled && (
                  <div className="space-y-4 rounded-md border border-white/15 bg-white/5 p-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="private_repo_container">Container Name</Label>
                        <Input
                          id="private_repo_container"
                          placeholder="privaterepo"
                          value={blobForm.private_repo_container}
                          onChange={(e) => setBlobForm({ ...blobForm, private_repo_container: e.target.value })}
                          data-testid="private-repo-container-input"
                        />
                        <p className="text-xs text-muted-foreground">
                          Dedicated container for auto-update sources. Azure rules: lowercase, 3-63 chars,
                          letters/digits/single-hyphens. Created automatically if it doesn't exist.
                        </p>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="private_repo_root">Root Folder <span className="text-muted-foreground">(optional)</span></Label>
                        <Input
                          id="private_repo_root"
                          placeholder="(none)"
                          value={blobForm.private_repo_root || ""}
                          onChange={(e) => setBlobForm({ ...blobForm, private_repo_root: e.target.value })}
                          data-testid="private-repo-root-input"
                        />
                        <p className="text-xs text-muted-foreground">
                          Optional prefix inside the container (e.g. <code>apps</code>). Leave empty if the
                          container itself is dedicated to Private Repository content.
                        </p>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="private_repo_path_template">Path Template</Label>
                      <Input
                        id="private_repo_path_template"
                        placeholder="{AppFamily}/{Version}/{Filename}"
                        value={blobForm.private_repo_path_template}
                        onChange={(e) => setBlobForm({ ...blobForm, private_repo_path_template: e.target.value })}
                        data-testid="private-repo-path-template-input"
                      />
                      <p className="text-xs text-muted-foreground">
                        Must include <code className="bg-white/10 text-white/90 px-1 rounded text-xs">{"{AppFamily}"}</code>,{" "}
                        <code className="bg-white/10 text-white/90 px-1 rounded text-xs">{"{Version}"}</code> and{" "}
                        <code className="bg-white/10 text-white/90 px-1 rounded text-xs">{"{Filename}"}</code>. Default is{" "}
                        <code className="bg-white/10 text-white/90 px-1 rounded text-xs">{"{AppFamily}/{Version}/{Filename}"}</code>.
                      </p>
                    </div>

                    <Alert className="bg-blue-50 border-blue-200">
                      <Info className="h-4 w-4 text-blue-700" />
                      <AlertTitle className="text-blue-900">Example layout</AlertTitle>
                      <AlertDescription className="mt-2 text-sm text-blue-900 space-y-1">
                        <div><code className="bg-white px-1.5 py-0.5 rounded text-xs">{(blobForm.private_repo_container || "privaterepo")}/{blobForm.private_repo_root ? blobForm.private_repo_root + "/" : ""}Sage Accounts/1.0/Sage.msi</code></div>
                        <div><code className="bg-white px-1.5 py-0.5 rounded text-xs">{(blobForm.private_repo_container || "privaterepo")}/{blobForm.private_repo_root ? blobForm.private_repo_root + "/" : ""}Sage Accounts/2.0/Sage.msi</code></div>
                        <div className="text-blue-800 pt-1 text-xs">
                          When you drop <code>Sage Accounts/3.0/Sage.msi</code> later, ALICE auto-packages it
                          using the same configuration as v2.0.
                        </div>
                      </AlertDescription>
                    </Alert>

                    <div className="flex items-center justify-between gap-4 flex-wrap pt-2 border-t border-white/10">
                      <div className="text-sm text-muted-foreground" data-testid="private-repo-stats">
                        {privateRepoStats ? (
                          <>
                            <strong className="text-white">{privateRepoStats.family_count}</strong> families ·{" "}
                            <strong className="text-white">{privateRepoStats.version_count}</strong> versions detected
                            {privateRepoStats.scanned_at && (
                              <span className="text-xs ml-2 opacity-70">
                                (last scanned {new Date(privateRepoStats.scanned_at).toLocaleString()})
                              </span>
                            )}
                          </>
                        ) : (
                          <span className="italic">No catalog yet — click Rescan to detect installers.</span>
                        )}
                      </div>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={rescanPrivateRepo}
                        disabled={rescanningPrivateRepo}
                        data-testid="private-repo-rescan-btn"
                      >
                        {rescanningPrivateRepo ? (
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        ) : (
                          <HardDrive className="h-4 w-4 mr-2" />
                        )}
                        Rescan now
                      </Button>
                    </div>

                    {privateRepoStats?.families?.length > 0 && (
                      <div className="rounded-md border border-white/10 bg-white/5 p-2" data-testid="private-repo-catalog">
                        <p className="text-xs text-muted-foreground mb-2 px-1">Detected families:</p>
                        <div className="flex flex-wrap gap-2">
                          {privateRepoStats.families.map((f) => (
                            <span
                              key={f.family_name}
                              className="text-xs bg-white/10 px-2 py-1 rounded"
                              data-testid={`private-repo-family-${f.family_name}`}
                              title={`${f.version_count} version(s); latest ${f.latest_version}`}
                            >
                              {f.family_name} <span className="opacity-60">· v{f.latest_version}</span>
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>

              <Separator />

              <div className="flex gap-2 pt-2 flex-wrap">
                <Button
                  variant="outline"
                  onClick={testBlobConnection}
                  disabled={testingConnection}
                  data-testid="test-connection-btn"
                >
                  {testingConnection ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <HardDrive className="h-4 w-4 mr-2" />
                  )}
                  Test Connection
                </Button>

                {blobForm.auth_mode === "entra" && (
                  <Button
                    variant="outline"
                    onClick={grantStorageAccess}
                    disabled={grantingStorageAccess}
                    data-testid="grant-storage-access-btn"
                    title="ALICE will assign 'Storage Blob Data Contributor' to its own service principal on this storage account using your Azure admin session — no PowerShell required."
                  >
                    {grantingStorageAccess ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Shield className="h-4 w-4 mr-2" />
                    )}
                    Grant ALICE access
                  </Button>
                )}

                <Button
                  onClick={saveBlobConfig}
                  disabled={savingBlobConfig}
                  data-testid="save-blob-config-btn"
                >
                  {savingBlobConfig ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <CheckCircle2 className="h-4 w-4 mr-2" />
                  )}
                  Save Configuration
                </Button>

                {blobConfig.configured && (
                  <Button
                    variant="destructive"
                    onClick={deleteBlobConfig}
                    data-testid="delete-blob-config-btn"
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Remove
                  </Button>
                )}
              </div>

              {/* Test result panel */}
              {testResult && (
                <div
                  data-testid="blob-test-result"
                  className={`mt-2 p-3 rounded-lg border ${
                    testResult.success
                      ? "bg-green-50 border-green-200 text-green-900"
                      : testResult.needs_role_assignment
                      ? "bg-yellow-50 border-yellow-200 text-yellow-900"
                      : "bg-red-50 border-red-200 text-red-900"
                  }`}
                >
                  <div className="text-sm">
                    {testResult.success ? "✅ " : testResult.needs_role_assignment ? "⚠️ " : "❌ "}
                    {testResult.message}
                  </div>
                </div>
              )}

              {/* Persistent banner when uploads are failing due to missing role */}
              {blobConfig.configured && blobConfig.last_upload_error?.reason === "needs_role_assignment" && (
                <div
                  data-testid="blob-upload-error-banner"
                  className="mt-2 p-3 rounded-lg border bg-yellow-50 border-yellow-200 text-yellow-900"
                >
                  <div className="text-sm">
                    <strong>Last upload was blocked.</strong>{" "}
                    {blobConfig.last_upload_error.message}{" "}
                    <span className="text-yellow-700">
                      ({blobConfig.last_upload_error.at})
                    </span>
                  </div>
                </div>
              )}

              {blobConfig.configured && (
                <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                  <div className="flex items-center gap-2 text-green-800">
                    <CheckCircle2 className="h-5 w-5" />
                    <span className="font-medium">Azure Blob Storage Active (Tenant-Wide)</span>
                    <span
                      className={`ml-2 text-xs px-2 py-0.5 rounded-full border ${
                        blobConfig.auth_mode === "entra"
                          ? "bg-white text-green-800 border-green-300"
                          : "bg-yellow-50 text-yellow-800 border-yellow-300"
                      }`}
                      data-testid="blob-auth-mode-badge"
                    >
                      {blobConfig.auth_mode === "entra" ? "Entra ID" : "Shared key"}
                    </span>
                  </div>
                  <p className="text-sm text-green-700 mt-1">
                    All users in this tenant will use: <code className="bg-green-100 px-1 rounded">{blobConfig.storage_account_name}/{blobConfig.container_name}</code>
                  </p>
                  <p className="text-xs text-green-600 mt-1">
                    {blobConfig.message}
                  </p>
                </div>
              )}

              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={() => window.open("https://portal.azure.com/#view/HubsExtension/BrowseResource/resourceType/Microsoft.Storage%2FStorageAccounts", "_blank")}
                >
                  <ExternalLink className="h-4 w-4 mr-2" />
                  Open Storage Accounts
                </Button>
                <Button
                  variant="outline"
                  onClick={() => window.open("https://learn.microsoft.com/en-us/azure/storage/blobs/storage-quickstart-blobs-portal", "_blank")}
                >
                  <ExternalLink className="h-4 w-4 mr-2" />
                  Documentation
                </Button>
              </div>
            </CardContent>
          </Card>
          </>)}

          {activeSection === "automation" && (<>
          {/* Deployment Automation Settings */}
          <Card data-testid="deployment-automation-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 font-['Chivo']">
                <Rocket className="h-5 w-5" />
                Deployment Automation
                {deploymentAutomation.auto_progress_enabled && (
                  <Badge variant="success" className="ml-2 bg-green-100 text-green-800">
                    <Zap className="h-3 w-3 mr-1" />
                    Enabled
                  </Badge>
                )}
              </CardTitle>
              <CardDescription>
                Automatically progress deployments through the stages of their pipeline based on success thresholds
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <Alert>
                <Info className="h-4 w-4" />
                <AlertTitle>How Automated Deployment Works</AlertTitle>
                <AlertDescription className="mt-2 space-y-2">
                  {/* Stage names and percentages come from each app's
                      deployment pipeline (Deployment pipelines card below),
                      so none are named here. */}
                  <ul className="list-disc list-inside space-y-1 text-sm">
                    <li><strong>Pre-UAT → UAT:</strong> when a pipeline has a Pre-UAT stage, it is always moved on to UAT by hand</li>
                    <li><strong>Leaving UAT:</strong> when all testers have <em>approved via email</em>, the package is marked ready for the first stage after UAT</li>
                    <li><strong>Between later stages:</strong> when the installation success rate for the current stage meets its threshold, the rollout moves on to the next stage of its pipeline</li>
                    <li><strong>Thresholds:</strong> the first threshold below applies when leaving the first stage after UAT, the second when leaving every later stage, unless a stage in the pipeline sets its own</li>
                    <li><strong>App owners are notified</strong> at each stage progression</li>
                    <li><strong>Per app:</strong> the switch below is the default. Use the Auto-Progress column on the Packages page to turn progression on or off for individual apps</li>
                  </ul>
                </AlertDescription>
              </Alert>

              <Separator />

              {/* Enable/Disable Toggle */}
              <div className="flex items-center justify-between p-4 border rounded-lg">
                <div className="space-y-1">
                  <Label className="text-base font-medium">Automatic Progression by Default</Label>
                  <p className="text-sm text-muted-foreground">
                    Advance deployments when success thresholds are met, for every app set to Default.
                    Apps set to On or Off on the Packages page ignore this switch.
                  </p>
                </div>
                {canDeployPackages ? (
                <Button
                  variant={deploymentAutomation.auto_progress_enabled ? "default" : "outline"}
                  size="lg"
                  onClick={() => setDeploymentAutomation(prev => ({
                    ...prev,
                    auto_progress_enabled: !prev.auto_progress_enabled
                  }))}
                  className={deploymentAutomation.auto_progress_enabled ? "bg-green-600 hover:bg-green-700" : ""}
                  data-testid="automation-toggle-btn"
                >
                  {deploymentAutomation.auto_progress_enabled ? (
                    <>
                      <ToggleRight className="h-5 w-5 mr-2" />
                      Enabled
                    </>
                  ) : (
                    <>
                      <ToggleLeft className="h-5 w-5 mr-2" />
                      Disabled
                    </>
                  )}
                </Button>
                ) : (
                  <span className="text-sm font-medium" data-testid="automation-status-readonly">
                    {deploymentAutomation.auto_progress_enabled ? "Enabled" : "Disabled"}
                  </span>
                )}
              </div>

              <Separator />

              {/* Threshold Settings */}
              <div className="space-y-4">
                <h4 className="font-medium">Success Thresholds</h4>
                
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="phase1Threshold">Leaving the first stage after UAT (%)</Label>
                    <Input
                      id="phase1Threshold"
                      type="number"
                      min="0"
                      max="100"
                      readOnly={!canDeployPackages}
                      value={deploymentAutomation.phase1_to_phase2_threshold}
                      onChange={(e) => setDeploymentAutomation(prev => ({
                        ...prev,
                        phase1_to_phase2_threshold: parseInt(e.target.value) || 0
                      }))}
                      data-testid="phase1-threshold-input"
                    />
                    <p className="text-xs text-muted-foreground">
                      % installation success rate required
                    </p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="phase2Threshold">Leaving each later stage (%)</Label>
                    <Input
                      id="phase2Threshold"
                      type="number"
                      min="0"
                      max="100"
                      readOnly={!canDeployPackages}
                      value={deploymentAutomation.phase2_to_phase3_threshold}
                      onChange={(e) => setDeploymentAutomation(prev => ({
                        ...prev,
                        phase2_to_phase3_threshold: parseInt(e.target.value) || 0
                      }))}
                      data-testid="phase2-threshold-input"
                    />
                    <p className="text-xs text-muted-foreground">
                      % installation success rate required
                    </p>
                  </div>
                </div>

                <Alert variant="default" className="bg-blue-50 border-blue-200">
                  <Info className="h-4 w-4 text-blue-600" />
                  <AlertDescription className="text-sm text-blue-800">
                    <strong>Leaving UAT</strong> uses tester email approvals (not installation %).
                    When all testers approve via email, the package becomes ready for the first stage after UAT.
                    A stage in a deployment pipeline can set its own threshold in place of these defaults.
                  </AlertDescription>
                </Alert>

                <div className="space-y-2 max-w-xs">
                  <Label htmlFor="checkInterval">Check Interval (minutes)</Label>
                  <Input
                    id="checkInterval"
                    type="number"
                    min="5"
                    max="60"
                    readOnly={!canDeployPackages}
                    value={deploymentAutomation.check_interval_minutes}
                    onChange={(e) => setDeploymentAutomation(prev => ({
                      ...prev,
                      check_interval_minutes: parseInt(e.target.value) || 15
                    }))}
                    data-testid="check-interval-input"
                  />
                  <p className="text-xs text-muted-foreground">
                    How often to check deployment status (5-60 minutes)
                  </p>
                </div>
              </div>

              <div className="flex gap-2 pt-4">
                {/* Hidden rather than disabled: the PUT needs deploy_packages. */}
                {canDeployPackages && (
                <Button
                  onClick={saveDeploymentAutomationSettings}
                  disabled={savingDeploymentAutomation}
                  data-testid="save-automation-btn"
                >
                  {savingDeploymentAutomation ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <CheckCircle2 className="h-4 w-4 mr-2" />
                  )}
                  Save Settings
                </Button>
                )}

                <Button
                  variant="outline"
                  onClick={triggerDeploymentCheck}
                  // Not gated on the default switch: apps can be opted in
                  // individually, and the server says when nothing is on.
                  disabled={checkingDeployments}
                  data-testid="check-deployments-btn"
                  className="bg-[#E5EFFA] text-[#3C02CC] hover:bg-[#BBFF71] hover:text-[#3C02CC] active:bg-[#BBFF71] active:text-[#3C02CC] font-semibold border-none"
                >
                  {checkingDeployments ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <RefreshCw className="h-4 w-4 mr-2" />
                  )}
                  Check Now
                </Button>
              </div>

              {deploymentAutomation.updated_at && (
                <p className="text-xs text-muted-foreground">
                  Last updated: {new Date(deploymentAutomation.updated_at).toLocaleString()}
                  {deploymentAutomation.updated_by && ` by ${deploymentAutomation.updated_by}`}
                </p>
              )}
            </CardContent>
          </Card>

          {/* Deployment pipelines: stage templates and the tenant default */}
          <DeploymentPipelinesCard getAuthHeaders={getAuthHeaders} />
          </>)}

          {activeSection === "data" && (<>
          {tenantHasOSPatch && <SyncScheduleCard />}
          {tenantHasOSPatch && <SyncHistoryCard />}
          {/* `scott/kpi-cycle-update2` — Windows Update catalog controls moved
              here from the OS Patch > Reports header. Catalog coverage is what
              lets ALICE date each device's build, so it belongs with the other
              data-plumbing controls rather than on a customer-facing
              compliance report. */}
          {tenantHasOSPatch && <WindowsUpdateCatalogCard />}
          {/* Database Maintenance - Super Admin Only */}
          {currentUserRole?.is_super_admin && (
          <Card data-testid="database-maintenance-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 font-['Chivo']">
                <Database className="h-5 w-5" />
                Database Maintenance
              </CardTitle>
              <CardDescription>
                Clean up duplicate entries and maintain data integrity
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="p-4 border rounded-lg space-y-3">
                <div>
                  <h4 className="font-medium">Normalize App Names</h4>
                  <p className="text-sm text-muted-foreground mt-1">
                    Rename app entries to their canonical names (e.g., "Node js" → "Node.js"). 
                    This runs automatically after sync and deployment.
                  </p>
                </div>
                <Button
                  variant="outline"
                  onClick={normalizeAppNames}
                  disabled={normalizingNames}
                  data-testid="normalize-names-btn"
                  className="bg-[#E5EFFA] text-[#3C02CC] hover:bg-[#BBFF71] hover:text-[#3C02CC] active:bg-[#BBFF71] active:text-[#3C02CC] font-semibold border-none"
                >
                  {normalizingNames ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <RefreshCw className="h-4 w-4 mr-2" />
                  )}
                  Normalize Names
                </Button>
              </div>
              
              <div className="p-4 border rounded-lg space-y-3">
                <div>
                  <h4 className="font-medium">Merge Duplicate Apps</h4>
                  <p className="text-sm text-muted-foreground mt-1">
                    Find and merge duplicate detected app entries. 
                    This runs automatically after normalization.
                  </p>
                </div>
                <Button
                  variant="outline"
                  onClick={mergeDuplicateApps}
                  disabled={mergingDuplicates}
                  data-testid="merge-duplicates-btn"
                  className="bg-[#E5EFFA] text-[#3C02CC] hover:bg-[#BBFF71] hover:text-[#3C02CC] active:bg-[#BBFF71] active:text-[#3C02CC] font-semibold border-none"
                >
                  {mergingDuplicates ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <RefreshCw className="h-4 w-4 mr-2" />
                  )}
                  Merge Duplicates
                </Button>
              </div>

              {/* CVE store drift repair (super-admin only, sits inside the
                  super-admin gated Database Maintenance card already). */}
              <div
                className="p-4 border border-amber-300 bg-amber-50/40 rounded-lg space-y-3"
                data-testid="cve-hard-reset-section"
              >
                <div className="flex items-start gap-3">
                  <ShieldAlert className="h-5 w-5 text-amber-600 mt-0.5 shrink-0" />
                  <div className="flex-1">
                    <h4 className="font-medium flex items-center gap-2">
                      CVE Store — Hard Reset
                      <Badge variant="outline" className="text-amber-700 border-amber-300 bg-amber-100/60 text-xs">
                        Emergency
                      </Badge>
                    </h4>
                    <p className="text-sm text-muted-foreground mt-1">
                      Use this when the <strong>Highest</strong> column is
                      blank across every family or Cosmos RU/s is spiking
                      during CVE sync. It wipes <code>cve_cpe_index</code>
                      (~150k rows) and clears the per-CPE delta cursors so
                      the next CVE sync does a genuine full re-pull instead
                      of a delta that returns 0 CVEs against an empty
                      <code> cve_records</code>.
                    </p>
                    <p className="text-xs text-muted-foreground mt-2">
                      Safe to run — no tenant data is touched. After this
                      completes, click <strong>Run Full CVE Sync</strong>
                      to repopulate.
                    </p>
                  </div>
                </div>

                {cveHardResetInfo && (
                  <div className="rounded-md border border-green-200 bg-green-50 p-3 text-sm">
                    <div className="font-medium text-green-800">Hard reset complete</div>
                    <ul className="mt-1 text-green-700 space-y-0.5 text-xs">
                      <li>• Index rows cleared: <strong>{cveHardResetInfo.cve_cpe_index_cleared}</strong></li>
                      <li>• Delta cursors cleared: <strong>{cveHardResetInfo.cpe_cursors_cleared}</strong></li>
                      <li>• Next sync will do a full per-CPE re-pull.</li>
                    </ul>
                  </div>
                )}

                <div className="flex flex-wrap gap-2">
                  <Button
                    variant="outline"
                    onClick={hardResetCveStore}
                    disabled={cveHardResetting || cveResyncing}
                    data-testid="cve-hard-reset-btn"
                    className="bg-amber-100 text-amber-900 hover:bg-amber-200 active:bg-amber-300 font-semibold border border-amber-300"
                  >
                    {cveHardResetting ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <ShieldAlert className="h-4 w-4 mr-2" />
                    )}
                    Hard Reset CVE Store
                  </Button>
                  <Button
                    variant="outline"
                    onClick={triggerCveResync}
                    disabled={cveHardResetting || cveResyncing}
                    data-testid="cve-run-full-sync-btn"
                    className="bg-[#E5EFFA] text-[#3C02CC] hover:bg-[#BBFF71] hover:text-[#3C02CC] active:bg-[#BBFF71] active:text-[#3C02CC] font-semibold border-none"
                  >
                    {cveResyncing ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <RefreshCw className="h-4 w-4 mr-2" />
                    )}
                    Run Full CVE Sync
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
          )}
          </>)}

          {activeSection === "general" && (<>
          {/* User Info */}
          <Card>
            <CardHeader>
              <CardTitle className="font-['Chivo']">Current User</CardTitle>
              <CardDescription>Your account information</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-1">
                  <Label className="text-muted-foreground">Name</Label>
                  <p className="font-medium">{user?.name}</p>
                </div>
                <div className="space-y-1">
                  <Label className="text-muted-foreground">Email</Label>
                  <p className="font-medium">{user?.email}</p>
                </div>
                <div className="space-y-1">
                  <Label className="text-muted-foreground">Role</Label>
                  <Badge className="bg-[#002FA7]">{user?.role}</Badge>
                </div>
                <div className="space-y-1">
                  <Label className="text-muted-foreground">Mode</Label>
                  <Badge variant={demoMode ? "outline" : "secondary"}>
                    {demoMode ? "Demo Mode" : "Production"}
                  </Badge>
                </div>
              </div>
            </CardContent>
          </Card>
          </>)}

          {activeSection === "access" && (<>
          {/* Users & Permissions - RBAC Management */}
          {!demoMode && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 font-['Chivo']">
                  <Users className="h-5 w-5" />
                  Users & Permissions
                </CardTitle>
                <CardDescription>
                  Manage who can access this tenant and their roles
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Check if user has permission to manage users */}
                {!currentUserRole?.permissions?.includes("manage_users") ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <Shield className="h-12 w-12 mx-auto mb-2 opacity-50" />
                    <p>You don't have permission to manage users.</p>
                    <p className="text-sm">Contact your tenant admin to request access.</p>
                  </div>
                ) : (
                  <>
                    {/* Invite User Form */}
                    <div className="space-y-4">
                      <h4 className="font-medium flex items-center gap-2">
                        <UserPlus className="h-4 w-4" />
                        Invite User
                      </h4>
                      <form onSubmit={handleInviteUser} className="flex flex-col gap-3">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                          <div className="space-y-1">
                            <Label htmlFor="inviteEmail">Email Address *</Label>
                            <Input
                              id="inviteEmail"
                              type="email"
                              placeholder="user@company.com"
                              value={inviteEmail}
                              onChange={(e) => setInviteEmail(e.target.value)}
                              required
                            />
                          </div>
                          <div className="space-y-1">
                            <Label htmlFor="inviteDisplayName">Display Name</Label>
                            <Input
                              id="inviteDisplayName"
                              type="text"
                              placeholder="John Doe"
                              value={inviteDisplayName}
                              onChange={(e) => setInviteDisplayName(e.target.value)}
                            />
                          </div>
                          <div className="space-y-1">
                            <Label htmlFor="inviteRole">Role</Label>
                            <Select value={inviteRole} onValueChange={setInviteRole}>
                              <SelectTrigger>
                                <SelectValue placeholder="Select role" />
                              </SelectTrigger>
                              <SelectContent>
                                {availableRoles.map((role) => (
                                  <SelectItem key={role.value} value={role.value}>
                                    <div className="flex flex-col">
                                      <span>{role.display_name}</span>
                                    </div>
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                        <Button type="submit" disabled={invitingUser} className="w-fit">
                          {invitingUser ? (
                            <>
                              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                              Inviting...
                            </>
                          ) : (
                            <>
                              <Mail className="h-4 w-4 mr-2" />
                              Send Invitation
                            </>
                          )}
                        </Button>
                      </form>
                    </div>
                    
                    <Separator />
                    
                    {/* User List */}
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <h4 className="font-medium flex items-center gap-2">
                          <Shield className="h-4 w-4" />
                          Tenant Users ({tenantUsers.length})
                        </h4>
                        <Button variant="ghost" size="sm" onClick={fetchTenantUsers} disabled={loadingUsers}>
                          <RefreshCw className={`h-4 w-4 ${loadingUsers ? 'animate-spin' : ''}`} />
                        </Button>
                      </div>
                      
                      {loadingUsers ? (
                        <div className="flex items-center justify-center py-8">
                          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                        </div>
                      ) : tenantUsers.length === 0 ? (
                        <div className="text-center py-8 text-muted-foreground">
                          <Users className="h-12 w-12 mx-auto mb-2 opacity-50" />
                          <p>No users found. Invite users above.</p>
                        </div>
                      ) : (
                        <div className="space-y-2">
                          {tenantUsers.map((u) => (
                            <div 
                              key={u.email} 
                              className="flex items-center justify-between p-3 bg-white/10 rounded-lg"
                            >
                              <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                                  <span className="text-primary font-medium">
                                    {(u.display_name || u.email)[0].toUpperCase()}
                                  </span>
                                </div>
                                <div>
                                  <div className="flex items-center gap-2">
                                    <span className="font-medium">{u.display_name || u.email.split("@")[0]}</span>
                                    {u.role === "tenant_admin" && (
                                      <Crown className="h-4 w-4 text-amber-500" />
                                    )}
                                  </div>
                                  <p className="text-sm text-muted-foreground">{u.email}</p>
                                </div>
                              </div>
                              <div className="flex items-center gap-3">
                                <Badge 
                                  variant="outline" 
                                  className={
                                    u.role === "tenant_admin" ? "border-red-300 text-red-700 bg-red-50" :
                                    u.role === "apps_manager" ? "border-blue-300 text-blue-700 bg-blue-50" :
                                    u.role === "os_patch_manager" ? "border-green-300 text-green-700 bg-green-50" :
                                    "border-white/20 text-white/90 bg-white/10"
                                  }
                                >
                                  {u.role_display_name}
                                </Badge>
                                <Badge variant={u.status === "active" ? "default" : "secondary"}>
                                  {u.status}
                                </Badge>
                                {u.email.toLowerCase() !== user?.email?.toLowerCase() && (
                                  <div className="flex items-center gap-1">
                                    <Select
                                      value={u.role}
                                      onValueChange={(newRole) => handleUpdateUserRole(u.email, newRole)}
                                    >
                                      <SelectTrigger className="w-[140px] h-8">
                                        <SelectValue />
                                      </SelectTrigger>
                                      <SelectContent>
                                        {availableRoles.map((role) => (
                                          <SelectItem key={role.value} value={role.value}>
                                            {role.display_name}
                                          </SelectItem>
                                        ))}
                                      </SelectContent>
                                    </Select>
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      className="h-8 w-8 text-destructive hover:text-destructive hover:bg-destructive/10"
                                      onClick={() => handleRemoveUser(u.email)}
                                    >
                                      <Trash2 className="h-4 w-4" />
                                    </Button>
                                  </div>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    
                    {/* Role Descriptions */}
                    <Separator />
                    <div className="space-y-3">
                      <h4 className="font-medium text-sm text-muted-foreground">Role Descriptions</h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                        {availableRoles.map((role) => (
                          <div key={role.value} className="flex items-start gap-2 p-2 rounded bg-white/10">
                            <Badge 
                              variant="outline" 
                              className={`flex-shrink-0 ${
                                role.color === "red" ? "border-red-300 text-red-700" :
                                role.color === "blue" ? "border-blue-300 text-blue-700" :
                                role.color === "green" ? "border-green-300 text-green-700" :
                                "border-white/20 text-white/90"
                              }`}
                            >
                              {role.display_name}
                            </Badge>
                            <span className="text-muted-foreground">{role.description}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          )}
          </>)}

          {activeSection === "admin" && (<>
          {/* Super Admin Section - Only visible to superalice@camwood.com */}
          {currentUserRole?.is_super_admin && (
            <Card className="border-purple-500/30 bg-purple-500/5">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 font-['Chivo']">
                  <Crown className="h-5 w-5 text-purple-500" />
                  Super Admin Panel
                  <Badge className="bg-purple-500 text-white ml-2">All Tenants</Badge>
                </CardTitle>
                <CardDescription>
                  Manage all tenants and users across the entire ALICE platform
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* System Stats */}
                {adminStats && (
                  <div className="alice-kpi-row grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="p-4 rounded-lg bg-purple-500/10 border border-purple-500/20">
                      <p className="text-2xl font-bold text-purple-600">{adminStats.tenants?.total || 0}</p>
                      <p className="text-sm text-muted-foreground">Total Tenants</p>
                    </div>
                    <div className="p-4 rounded-lg bg-green-500/10 border border-green-500/20">
                      <p className="text-2xl font-bold text-green-600">{adminStats.tenants?.active || 0}</p>
                      <p className="text-sm text-muted-foreground">Active</p>
                    </div>
                    <div className="p-4 rounded-lg bg-yellow-500/10 border border-yellow-500/20">
                      <p className="text-2xl font-bold text-yellow-600">{adminStats.tenants?.pending || 0}</p>
                      <p className="text-sm text-muted-foreground">Pending Setup</p>
                    </div>
                    <div className="p-4 rounded-lg bg-blue-500/10 border border-blue-500/20">
                      <p className="text-2xl font-bold text-blue-600">{adminStats.users?.total || 0}</p>
                      <p className="text-sm text-muted-foreground">Total Users</p>
                    </div>
                  </div>
                )}
                
                <Separator />
                
                {/* Tenants List */}
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h4 className="font-medium flex items-center gap-2">
                      <Building2 className="h-4 w-4" />
                      All Tenants ({allTenants.length})
                    </h4>
                    <Button variant="ghost" size="sm" onClick={fetchAllTenants} disabled={loadingTenants}>
                      <RefreshCw className={`h-4 w-4 ${loadingTenants ? 'animate-spin' : ''}`} />
                    </Button>
                  </div>
                  
                  {loadingTenants ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                    </div>
                  ) : allTenants.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground">
                      <Building2 className="h-12 w-12 mx-auto mb-2 opacity-50" />
                      <p>No tenants registered yet.</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {allTenants.map((tenant) => (
                        <div 
                          key={tenant.tenant_id} 
                          className="p-4 bg-white/10 rounded-lg border"
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                                <Building2 className="h-5 w-5 text-primary" />
                              </div>
                              <div>
                                <div className="flex items-center gap-2">
                                  <span className="font-medium">{tenant.display_name || "Unnamed Tenant"}</span>
                                  <Badge 
                                    variant="outline" 
                                    className={
                                      tenant.status === "active" ? "border-green-300 text-green-700 bg-green-50" :
                                      tenant.status === "pending_setup" ? "border-yellow-300 text-yellow-700 bg-yellow-50" :
                                      "border-white/20 text-white/90 bg-white/10"
                                    }
                                  >
                                    {tenant.status}
                                  </Badge>
                                  {tenant.connection_type === "sccm" && (
                                    <Badge
                                      className="bg-sky-500 text-white"
                                      title="Onboarded from an SCCM export - no Intune connection"
                                      data-testid={`tenant-mode-sccm-${tenant.tenant_id}`}
                                    >
                                      SCCM
                                    </Badge>
                                  )}
                                  {tenant.connection_type !== "sccm" && (
                                    <Badge
                                      variant="outline"
                                      className="text-xs"
                                      title="ALICE plan"
                                      data-testid={`tenant-plan-${tenant.tenant_id}`}
                                    >
                                      {serviceTierShortLabel(tenant.service_tier || "full")}
                                    </Badge>
                                  )}
                                  {tenant.pending_service_tier && (
                                    <Badge
                                      className="bg-amber-500 text-white text-xs"
                                      title="Waiting for one of the tenant's admins to approve the new plan's permissions"
                                      data-testid={`tenant-plan-pending-${tenant.tenant_id}`}
                                    >
                                      Pending: {serviceTierShortLabel(tenant.pending_service_tier)}
                                    </Badge>
                                  )}
                                </div>
                                <p className="text-sm text-muted-foreground">
                                  {tenant.user_count} user{tenant.user_count !== 1 ? 's' : ''} • 
                                  {tenant.primary_domain && ` ${tenant.primary_domain} •`}
                                  ID: {tenant.tenant_id.substring(0, 8)}...
                                </p>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => {
                                  if (selectedTenantUsers[tenant.tenant_id]) {
                                    setSelectedTenantUsers(prev => {
                                      const newState = { ...prev };
                                      delete newState[tenant.tenant_id];
                                      return newState;
                                    });
                                  } else {
                                    fetchTenantUsersAdmin(tenant.tenant_id);
                                  }
                                }}
                              >
                                <Users className="h-4 w-4 mr-1" />
                                {selectedTenantUsers[tenant.tenant_id] ? "Hide Users" : "View Users"}
                              </Button>
                              <Button
                                variant="destructive"
                                size="sm"
                                onClick={() => handleDeleteTenantAdmin(tenant.tenant_id, tenant.display_name)}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </div>
                          
                          {/* Expandable user list */}
                          {selectedTenantUsers[tenant.tenant_id] && (
                            <div className="mt-4 pl-13 border-t pt-4">
                              <h5 className="text-sm font-medium mb-2">Users in this tenant:</h5>
                              <div className="space-y-2">
                                {selectedTenantUsers[tenant.tenant_id].length === 0 ? (
                                  <p className="text-sm text-muted-foreground">No users</p>
                                ) : (
                                  selectedTenantUsers[tenant.tenant_id].map((u) => (
                                    <div key={u.email} className="flex items-center justify-between text-sm p-2 bg-background rounded">
                                      <div className="flex items-center gap-2">
                                        <span className="font-medium">{u.display_name || u.email}</span>
                                        <Badge variant="outline" className="text-xs">
                                          {u.role_display_name}
                                        </Badge>
                                      </div>
                                      <span className="text-muted-foreground">{u.email}</span>
                                    </div>
                                  ))
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          )}
          

          {/* Offline SCCM Tenant Onboarding - Super Admin Only */}
          {currentUserRole?.is_super_admin && (
            <Card className="border-sky-500/30 bg-sky-500/5" data-testid="offline-sccm-tenant-card">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 font-['Chivo']">
                  <Server className="h-5 w-5 text-sky-500" />
                  Onboard SCCM Tenant (Offline)
                  <Badge className="bg-sky-500 text-white ml-2">Super Admin</Badge>
                </CardTitle>
                <CardDescription>
                  Create a tenant for a customer who does not use Intune. No Entra app
                  registration, no admin consent, and the customer does not need to sign
                  in &mdash; you import their estate from SCCM SQL exports afterwards.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-5">
                <Alert className="bg-sky-50 border-sky-200">
                  <Info className="h-4 w-4 text-sky-600" />
                  <AlertTitle className="text-sky-800">What this tenant can and cannot do</AlertTitle>
                  <AlertDescription className="text-sky-700 mt-2 space-y-2">
                    <div className="grid gap-1 text-sm sm:grid-cols-2">
                      <ul className="list-disc list-inside space-y-0.5">
                        <li>Application discovery &amp; rationalisation</li>
                        <li>Automation potential (Winget matching)</li>
                        <li>Application packaging</li>
                      </ul>
                      <ul className="list-disc list-inside space-y-0.5 text-sky-900/70">
                        <li><strong>No</strong> deployment to Intune</li>
                        <li><strong>No</strong> OS Patch Management</li>
                        <li><strong>No</strong> Intune discovery sync</li>
                      </ul>
                    </div>
                    <p className="text-xs">
                      These limits are enforced by the API, not just hidden in the UI.
                    </p>
                  </AlertDescription>
                </Alert>

                {createdOfflineTenant && (
                  <div
                    className="p-4 rounded-lg border border-green-300 bg-green-50 space-y-3"
                    data-testid="offline-sccm-tenant-created"
                  >
                    <h4 className="font-medium flex items-center gap-2 text-green-800">
                      <CheckCircle2 className="h-4 w-4 text-green-600" />
                      Tenant created &mdash; {createdOfflineTenant.display_name}
                    </h4>
                    <div className="grid gap-2 text-sm text-green-900">
                      <div className="flex items-center justify-between gap-3">
                        <span className="text-green-700">Tenant ID:</span>
                        <div className="flex items-center gap-2">
                          <code className="bg-white px-2 py-0.5 rounded text-xs break-all">
                            {createdOfflineTenant.tenant_id}
                          </code>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              navigator.clipboard?.writeText(createdOfflineTenant.tenant_id);
                              toast.success("Tenant ID copied");
                            }}
                            data-testid="copy-offline-tenant-id"
                          >
                            <Copy className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-green-700">Mode:</span>
                        <Badge className="bg-sky-500 text-white">
                          {createdOfflineTenant.connection_type}
                        </Badge>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-green-700">Status:</span>
                        <Badge className="bg-green-100 text-green-800">
                          {createdOfflineTenant.status}
                        </Badge>
                      </div>
                    </div>
                    <p className="text-xs text-green-800">
                      Switch to this tenant with the tenant selector in the sidebar, then
                      import the customer&apos;s SCCM export (ARP, RIDARP and Devices).
                    </p>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCreatedOfflineTenant(null)}
                      data-testid="dismiss-offline-tenant-result"
                    >
                      Dismiss
                    </Button>
                  </div>
                )}

                <form onSubmit={handleCreateOfflineTenant} className="space-y-4">
                  <div className="grid gap-4 sm:grid-cols-2">
                    <div className="space-y-2 sm:col-span-2">
                      <Label htmlFor="offline-display-name">
                        Organisation name <span className="text-red-500">*</span>
                      </Label>
                      <Input
                        id="offline-display-name"
                        placeholder="Contoso Ltd"
                        value={offlineTenantForm.display_name}
                        onChange={(e) => setOfflineTenantForm((f) => ({ ...f, display_name: e.target.value }))}
                        disabled={creatingOfflineTenant}
                        data-testid="offline-tenant-display-name"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="offline-admin-email">Customer contact email</Label>
                      <Input
                        id="offline-admin-email"
                        type="email"
                        placeholder="it-admin@contoso.com"
                        value={offlineTenantForm.admin_email}
                        onChange={(e) => setOfflineTenantForm((f) => ({ ...f, admin_email: e.target.value }))}
                        disabled={creatingOfflineTenant}
                        data-testid="offline-tenant-admin-email"
                      />
                      <p className="text-xs text-muted-foreground">
                        If they later sign in with this address they are picked up as the
                        tenant admin.
                      </p>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="offline-domain">Primary domain</Label>
                      <Input
                        id="offline-domain"
                        placeholder="contoso.com"
                        value={offlineTenantForm.domain}
                        onChange={(e) => setOfflineTenantForm((f) => ({ ...f, domain: e.target.value }))}
                        disabled={creatingOfflineTenant}
                        data-testid="offline-tenant-domain"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="offline-technical-contact">Technical contact</Label>
                      <Input
                        id="offline-technical-contact"
                        placeholder="Name or email"
                        value={offlineTenantForm.technical_contact}
                        onChange={(e) => setOfflineTenantForm((f) => ({ ...f, technical_contact: e.target.value }))}
                        disabled={creatingOfflineTenant}
                        data-testid="offline-tenant-technical-contact"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="offline-entra-id">Entra tenant ID (optional)</Label>
                      <Input
                        id="offline-entra-id"
                        placeholder="00000000-0000-0000-0000-000000000000"
                        value={offlineTenantForm.linked_entra_tenant_id}
                        onChange={(e) => setOfflineTenantForm((f) => ({ ...f, linked_entra_tenant_id: e.target.value }))}
                        disabled={creatingOfflineTenant}
                        data-testid="offline-tenant-entra-id"
                      />
                      <p className="text-xs text-muted-foreground">
                        Recorded only so this tenant can be reconciled if the customer
                        later onboards themselves. Never used to acquire tokens.
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <Button
                      type="submit"
                      className="bg-sky-600 hover:bg-sky-700 text-white"
                      disabled={creatingOfflineTenant || offlineTenantForm.display_name.trim().length < 2}
                      data-testid="create-offline-tenant-btn"
                    >
                      {creatingOfflineTenant ? (
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <Building2 className="h-4 w-4 mr-2" />
                      )}
                      Create SCCM Tenant
                    </Button>
                    <p className="text-xs text-muted-foreground">
                      ALICE generates the tenant ID. The tenant is created active &mdash;
                      there is no consent step to complete.
                    </p>
                  </div>
                </form>
              </CardContent>
            </Card>
          )}

          {/* Database Management - Super Admin Only */}
          {currentUserRole?.is_super_admin && (
          <Card data-testid="database-management-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 font-['Chivo']">
                <Database className="h-5 w-5" />
                Database Management
              </CardTitle>
              <CardDescription>
                Import reference databases for app rationalization and CVE matching
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* AppsDatabase */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="font-medium">Apps Database</h4>
                    <p className="text-sm text-muted-foreground">
                      Reference data for categorizing apps (System Components, Drivers, etc.)
                    </p>
                  </div>
                  {appsDbStats?.imported ? (
                    <Badge variant="outline" className="bg-green-50 text-green-700 border-green-300">
                      <CheckCircle2 className="h-3 w-3 mr-1" />
                      {appsDbStats.total?.toLocaleString()} apps
                    </Badge>
                  ) : (
                    <Badge variant="outline" className="text-muted-foreground">
                      Not imported
                    </Badge>
                  )}
                </div>
                
                {appsDbStats?.imported && appsDbStats?.categories && (
                  <div className="flex flex-wrap gap-2 text-xs">
                    <span className="px-2 py-1 bg-white/15 rounded">
                      System: {appsDbStats.categories.system_component || 0}
                    </span>
                    <span className="px-2 py-1 bg-white/15 rounded">
                      Drivers: {appsDbStats.categories.device_driver || 0}
                    </span>
                    <span className="px-2 py-1 bg-white/15 rounded">
                      Non-Business: {appsDbStats.categories.non_business || 0}
                    </span>
                    <span className="px-2 py-1 bg-white/15 rounded">
                      Redistributable: {appsDbStats.categories.redistributable || 0}
                    </span>
                  </div>
                )}
                
                <div className="flex gap-2">
                  <input
                    type="file"
                    ref={appsDbFileRef}
                    onChange={handleAppsDbUpload}
                    accept=".csv"
                    className="hidden"
                  />
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => appsDbFileRef.current?.click()}
                      disabled={uploadingAppsDb}
                    >
                      <Upload className="h-4 w-4 mr-2" />
                      {uploadingAppsDb ? "Importing..." : "Import AppsDatabase CSV"}
                    </Button>
                    {appsDbStats?.imported && (
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={handleReapplyRationalization}
                        disabled={applyingRationalization}
                        data-testid="reapply-rationalization-btn"
                      >
                        <RefreshCw className={`h-4 w-4 mr-2 ${applyingRationalization ? 'animate-spin' : ''}`} />
                        {applyingRationalization ? "Applying..." : "Re-apply Rationalisation"}
                      </Button>
                    )}
                  </div>
                </div>
              </div>
              
              <Separator />
              
              {/* Winget Database */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="font-medium">Winget Database</h4>
                    <p className="text-sm text-muted-foreground">
                      {wingetDbStats?.imported 
                        ? `${wingetDbStats.total.toLocaleString()} packages loaded`
                        : "Not imported"}
                    </p>
                  </div>
                  <input
                    type="file"
                    accept=".csv"
                    className="hidden"
                    ref={wingetDbFileRef}
                    onChange={handleWingetDbUpload}
                  />
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => wingetDbFileRef.current?.click()}
                    disabled={uploadingWingetDb}
                  >
                    <Upload className="h-4 w-4 mr-2" />
                    {uploadingWingetDb ? "Importing..." : "Import Winget CSV"}
                  </Button>
                </div>
                
                <Alert>
                  <Info className="h-4 w-4" />
                  <AlertDescription className="text-xs">
                    Winget database provides latest package versions for upgrade recommendations. Export from winget or use the winget-pkgs repository.
                  </AlertDescription>
                </Alert>
              </div>
            </CardContent>
          </Card>
          )}
          </>)}

          {activeSection === "data" && (<>
          {/* Tenant Administration - Super Admin Only */}
          {!demoMode && (
            <Card data-testid="tenant-admin-card">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 font-['Chivo']">
                  <Building2 className="h-5 w-5" />
                  Tenant Administration
                  <Badge variant="outline" className="ml-2 text-amber-600 border-amber-300 bg-amber-50">
                    <UserCog className="h-3 w-3 mr-1" />
                    Super Admin
                  </Badge>
                </CardTitle>
                <CardDescription>
                  Manage tenant registration and Graph API connectivity
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Current Tenant Info */}
                {tenantInfo?.registered && (
                  <div className="p-4 bg-white/10 border rounded-lg space-y-3">
                    <h4 className="font-medium flex items-center gap-2">
                      <CheckCircle2 className="h-4 w-4 text-green-600" />
                      Tenant Connected
                    </h4>
                    <div className="grid gap-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Tenant ID:</span>
                        <code className="bg-white/20 px-2 py-0.5 rounded text-xs">
                          {tenantInfo.tenant_id}
                        </code>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Status:</span>
                        <Badge variant={tenantInfo.status === "active" ? "default" : "secondary"} className="bg-green-100 text-green-800">
                          {tenantInfo.status}
                        </Badge>
                      </div>
                      {tenantInfo.display_name && (
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Organization:</span>
                          <span className="font-medium">{tenantInfo.display_name}</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Test Connection */}
                <div className="space-y-3">
                  <h4 className="font-medium">Graph API Connection</h4>
                  <p className="text-sm text-muted-foreground">
                    Test connectivity to your tenant's Microsoft Graph API using client credentials.
                  </p>
                  
                  {graphConnectionStatus && (
                    <div className={`p-3 rounded-lg border ${
                      graphConnectionStatus.status === "success" 
                        ? "bg-green-50 border-green-200" 
                        : "bg-red-50 border-red-200"
                    }`}>
                      <div className="flex items-center gap-2">
                        {graphConnectionStatus.status === "success" ? (
                          <CheckCircle2 className="h-4 w-4 text-green-600" />
                        ) : (
                          <XCircle className="h-4 w-4 text-red-600" />
                        )}
                        <span className={graphConnectionStatus.status === "success" ? "text-green-800" : "text-red-800"}>
                          {graphConnectionStatus.message}
                        </span>
                      </div>
                      {graphConnectionStatus.organization && (
                        <p className="text-sm text-green-700 mt-1 ml-6">
                          Organization: {graphConnectionStatus.organization}
                        </p>
                      )}
                    </div>
                  )}
                  
                  <Button
                    variant="outline"
                    onClick={handleTestConnection}
                    disabled={testingGraphConnection || !tenantInfo?.registered}
                    data-testid="test-graph-connection-btn"
                  >
                    {testingGraphConnection ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <RefreshCw className="h-4 w-4 mr-2" />
                    )}
                    Test Graph Connection
                  </Button>
                </div>

                <Separator />

                {/* Cancel Stuck Sync */}
                <div className="space-y-3">
                  <h4 className="font-medium text-amber-700">Sync Management</h4>
                  <Alert className="bg-amber-50 border-amber-200">
                    <AlertTriangle className="h-4 w-4 text-amber-600" />
                    <AlertTitle className="text-amber-800">Cancel Stuck Sync</AlertTitle>
                    <AlertDescription className="text-amber-700 mt-2 space-y-2">
                      <p>Use this if a sync is stuck showing "in progress" but not completing.</p>
                      <p className="text-xs">This resets the sync status so you can start a new sync.</p>
                    </AlertDescription>
                  </Alert>
                  
                  <Button
                    variant="outline"
                    className="border-amber-500 text-amber-700 hover:bg-amber-50"
                    onClick={handleCancelSync}
                    disabled={cancellingSync}
                    data-testid="cancel-sync-btn"
                  >
                    {cancellingSync ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <XCircle className="h-4 w-4 mr-2" />
                    )}
                    Cancel Stuck Sync
                  </Button>

                  <Alert className="bg-sky-50 border-sky-200 mt-4">
                    <RefreshCw className="h-4 w-4 text-sky-600" />
                    <AlertTitle className="text-sky-800">Force Full CVE Keyword Sweep</AlertTitle>
                    <AlertDescription className="text-sky-700 mt-2 space-y-2">
                      <p>Resets the per-CPE keyword cursors so the next NVD sync re-pulls the full keyword corpus for every tracked product.</p>
                      <p className="text-xs">Use when NVD ships an <code>affected</code>-only CVE for a product you're already watching and the routine delta sweep hasn't picked it up yet. The next sync will take ~10-15 minutes; subsequent syncs return to fast delta mode.</p>
                    </AlertDescription>
                  </Alert>

                  <Button
                    variant="outline"
                    className="border-sky-500 text-sky-700 hover:bg-sky-50"
                    onClick={handleForceFullSweep}
                    disabled={forcingFullSweep}
                    data-testid="force-full-sweep-btn"
                  >
                    {forcingFullSweep ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <RefreshCw className="h-4 w-4 mr-2" />
                    )}
                    Force Full Sweep on Next Sync
                  </Button>
                </div>

                <Separator />

                {/* Reset Tenant -- tenant admins only (super admins count as
                    tenant admins). The backend refuses everyone else since
                    the 20 Oct 2026 security fix, so the control is hidden
                    rather than left to fail. */}
                {isAdmin && (
                <div className="space-y-3">
                  <h4 className="font-medium text-red-700">Danger Zone</h4>
                  <Alert className="bg-red-50 border-red-200">
                    <AlertTriangle className="h-4 w-4 text-red-600" />
                    <AlertTitle className="text-red-800">Reset Tenant Registration</AlertTitle>
                    <AlertDescription className="text-red-700 mt-2 space-y-2">
                      <p>This will delete your tenant registration from ALICE. Use this to:</p>
                      <ul className="list-disc list-inside text-sm space-y-1">
                        <li>Re-test the onboarding flow</li>
                        <li>Troubleshoot consent issues</li>
                        <li>Start fresh with a new configuration</li>
                      </ul>
                      <p className="text-xs mt-2">
                        <strong>Note:</strong> This does NOT revoke consent in Azure AD. To fully disconnect, 
                        also remove ALICE from Azure AD → Enterprise Applications.
                      </p>
                    </AlertDescription>
                  </Alert>
                  
                  <Button
                    variant="destructive"
                    onClick={handleResetTenant}
                    disabled={resettingTenant || !tenantInfo?.registered}
                    data-testid="reset-tenant-btn"
                  >
                    {resettingTenant ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Trash2 className="h-4 w-4 mr-2" />
                    )}
                    Reset Tenant Registration
                  </Button>
                </div>
                )}
              </CardContent>
            </Card>
          )}
          </>)}
        </div>
      </main>
    </div>
  );
}
